<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onUnmounted,
  useTemplateRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { formatDistanceToNow, isSameDay, isToday, isYesterday, subDays } from 'date-fns';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { vOnClickOutside } from '@vueuse/components';
import internalChatRooms from 'dashboard/api/internalChatRooms';
import FunnelAPI from 'dashboard/api/funnel';
import KanbanAPI from 'dashboard/api/kanban';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import InternalChatPeerKanbanPanel from './components/InternalChatPeerKanbanPanel.vue';
import InternalChatAssignFunnelPanel from './components/InternalChatAssignFunnelPanel.vue';
import InternalChatConversationDetailsPanel from './components/InternalChatConversationDetailsPanel.vue';

/** Textos fixos em pt_BR (modal de edição do nome do grupo) */
const INTERNAL_CHAT_EDIT_GROUP_NAME_PT = {
  TITLE: 'Editar nome do grupo',
  LABEL: 'Novo nome',
  PLACEHOLDER: 'Digite o nome do grupo',
  SAVE: 'Salvar',
  CANCEL: 'Cancelar',
  ARIA_EDIT: 'Editar nome do grupo',
  ERROR: 'Não foi possível atualizar o nome do grupo',
};

/** Textos fixos em pt_BR — gestão de membros do grupo */
const INTERNAL_CHAT_MANAGE_MEMBERS_PT = {
  TITLE: 'Membros do grupo',
  IN_GROUP: 'No grupo',
  ADD_SECTION: 'Adicionar participantes',
  SEARCH: 'Buscar agente…',
  ADD: 'Adicionar',
  REMOVE: 'Remover',
  REMOVE_ARIA: 'Remover do grupo',
  ADMIN: 'Administrador',
  LOADING: 'Carregando…',
  EMPTY_ADD: 'Não há mais agentes para adicionar',
  ERROR_LOAD: 'Não foi possível carregar os membros',
  ERROR_ADD: 'Não foi possível adicionar o participante',
  ERROR_REMOVE: 'Não foi possível remover o participante',
  CLOSE: 'Fechar',
  ARIA_OPEN: 'Gerenciar membros do grupo',
};

/** Textos fixos em pt_BR — atribuir cards do funil ao agente da conversa direta */
const INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT = {
  FUNNEL_LABEL: 'Funil',
  FUNNEL_PLACEHOLDER: 'Selecione o funil',
  ITEMS_HINT:
    'Selecione um ou mais cards para atribuir ao agente desta conversa.',
  EMPTY_ITEMS: 'Nenhum card disponível neste funil.',
  MODE_LABEL: 'Modo',
  MODE_ADD: 'Adicionar ao agente (mantém demais)',
  MODE_REPLACE: 'Substituir agentes atribuídos',
  CONFIRM: 'Atribuir',
  CANCEL: 'Cancelar',
  LOADING_ITEMS: 'Carregando cards…',
  SUCCESS: 'Cards atribuídos com sucesso.',
  ERROR_LOAD_ITEMS: 'Não foi possível carregar os cards.',
  ERROR_ASSIGN: 'Não foi possível atribuir os cards.',
};

/** Textos fixos em pt_BR — listar itens do kanban atribuídos ao agente da conversa */
const INTERNAL_CHAT_PEER_KANBAN_PT = {
  TITLE: 'Itens deste agente',
  FUNNEL_LABEL: 'Funil',
  STAGE_LABEL: 'Etapa',
  STAGE_ALL: 'Todas as etapas',
  HINT:
    'Lista filtrada por funil, etapa (opcional) e atribuição ao agente desta conversa.',
  LOADING: 'Carregando…',
  EMPTY: 'Nenhum card encontrado com estes filtros.',
  CLOSE: 'Fechar',
  ERROR_LOAD: 'Não foi possível carregar os cards.',
  OPEN_DETAIL_ARIA: 'Abrir detalhes do item no quadro',
  UPDATED: 'Atualizado',
  STAGE_SINCE: 'Na etapa há',
  ATTACHMENTS: 'anexos',
  CONVERSATION: 'Conversa',
  CARD_ID: 'Card',
};

/** Textos fixos em pt_BR — painel de detalhes da conversa */
const INTERNAL_CHAT_CONVERSATION_DETAILS_PT = {
  PANEL_TITLE: 'Detalhes da conversa',
  INFO_SECTION_TITLE: 'Infos da conversa',
  OPEN_LINK: 'Detalhes da conversa',
  CLOSE: 'Fechar',
  KIND: 'Tipo',
  NAME: 'Nome',
  PARTICIPANTS: 'Participantes',
  CREATED_BY: 'Criado por',
  LAST_ACTIVITY: 'Última atividade',
  YOU: 'Você',
  EMPTY_MEMBERS: 'Nenhum participante encontrado',
  SHARED_FILES: 'Arquivos compartilhados',
  EMPTY_SHARED_FILES: 'Nenhum arquivo compartilhado nesta conversa',
};

/** Textos fixos em pt_BR — mensagem fixada */
const INTERNAL_CHAT_PINNED_MESSAGE_PT = {
  CONTEXT_PIN: 'Fixar mensagem',
  CONTEXT_PIN_ANNOUNCEMENT: 'Fixar como anuncio',
  CONTEXT_UNPIN: 'Desafixar mensagem',
  CONFIRM_TITLE: 'Fixar mensagem',
  CONFIRM_DESCRIPTION: 'Escolha como deseja fixar esta mensagem.',
  CONFIRM_ACTION: 'Fixar',
  MODE_NORMAL: 'Fixacao normal',
  MODE_ANNOUNCEMENT: 'Fixar como anuncio',
  BANNER_TITLE: 'Mensagem fixada',
  BANNER_TITLE_ANNOUNCEMENT: 'Anuncio fixado',
  BANNER_EMPTY: 'Mensagem sem texto',
  BADGE_ANNOUNCEMENT: 'Anuncio',
  ERROR_PIN: 'Nao foi possivel atualizar a mensagem fixada',
};

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { isStacklab } = useConfig();
const store = useStore();
const { getPlainText } = useMessageFormatter();

const currentUser = useMapGetter('getCurrentUser');
const verifiedAgents = useMapGetter('agents/getVerifiedAgents');
const kanbanConfigTitle = useMapGetter('kanban/getKanbanTitle');

const peerKanbanButtonLabel = computed(() => 'Gestão do CRM');

const peerKanbanButtonAria = computed(() => {
  const title = (kanbanConfigTitle.value || 'Kanban').trim();
  return `Ver itens do ${title} atribuídos a este agente`;
});

const assignFunnelCardButtonLabel = computed(() => 'Atribuir item do CRM');

const assignFunnelCardButtonAria = computed(() => {
  const title = (kanbanConfigTitle.value || 'Kanban').trim();
  return `Atribuir card do ${title} a este agente`;
});

const assignFunnelCardDialogTitle = computed(() => {
  const title = (kanbanConfigTitle.value || 'Kanban').trim();
  return `Atribuir cards do ${title}`;
});

const rooms = ref([]);
const roomsCountByKind = ref({
  all: 0,
  direct: 0,
  multi_user: 0,
});
const messages = ref([]);
const selectedRoomId = ref(null);
const loadingRooms = ref(false);
const loadingMessages = ref(false);
const sending = ref(false);
const creatingDirect = ref(false);
const creatingGroup = ref(false);
const leavingGroup = ref(false);
const savingGroupName = ref(false);
const savingGroupAvatar = ref(false);
const editGroupName = ref('');
const manageMembersAgentSearchQuery = ref('');
const loadingManageMembers = ref(false);
const memberActionLoadingId = ref(null);
const composerMessage = ref('');
const composerAttachments = ref([]);
const composerReplyToMessage = ref(null);

const directUserId = ref('');
const directAgentSearchQuery = ref('');
const groupAgentSearchQuery = ref('');
const groupName = ref('');
const groupMemberIds = ref([]);

const directDialogRef = useTemplateRef('directDialogRef');
const groupDialogRef = useTemplateRef('groupDialogRef');
const leaveGroupDialogRef = useTemplateRef('leaveGroupDialogRef');
const editGroupNameDialogRef = useTemplateRef('editGroupNameDialogRef');
const manageMembersDialogRef = useTemplateRef('manageMembersDialogRef');
const messagesPaneRef = useTemplateRef('messagesPaneRef');
const composerAttachmentInputRef = useTemplateRef('composerAttachmentInputRef');
const composerEditorRef = useTemplateRef('composerEditorRef');
const editMessageDialogRef = useTemplateRef('editMessageDialogRef');
const deleteMessageDialogRef = useTemplateRef('deleteMessageDialogRef');
const resendMessageDialogRef = useTemplateRef('resendMessageDialogRef');
const messageContextMenuRef = useTemplateRef('messageContextMenuRef');
const pinMessageDialogRef = useTemplateRef('pinMessageDialogRef');

const accountFunnels = ref([]);
const assignSelectedFunnelId = ref('');
const assignKanbanItems = ref([]);
const assignSelectedItemIds = ref([]);
const assignLoadingItems = ref(false);
const assignSubmitting = ref(false);
const assignMode = ref('add');

const peerKanbanFunnelId = ref('');
const peerKanbanStageId = ref('');
const peerKanbanItems = ref([]);
const peerKanbanLoading = ref(false);
const showPeerKanbanPanel = ref(false);
const showAssignFunnelPanel = ref(false);
const showConversationDetailsPanel = ref(false);

const showNewConversationMenu = ref(false);
const roomsFilterTab = ref('all');
const roomSearchQuery = ref('');
const showMessageContextMenu = ref(false);
const messageContextMenuX = ref(0);
const messageContextMenuY = ref(0);
const contextMessageId = ref(null);
const editingMessageId = ref(null);
const editingMessageContent = ref('');
const savingMessageEdit = ref(false);
const deletingMessage = ref(false);
const resendingMessage = ref(false);
const resendingMessageId = ref(null);
const reactionPickerMessageId = ref(null);
const pinTargetMessageId = ref(null);
const pinConfirmMode = ref('normal');
const pinnedBannerIndex = ref(0);
const highlightedMessageId = ref(null);
let highlightedMessageTimeout = null;

const INTERNAL_CHAT_REACTION_EMOJIS = ['👍', '❤️', '😂', '😮'];

const newConversationMenuItems = computed(() => [
  {
    action: 'new_conversation',
    value: 'direct',
    label: t('INTERNAL_CHAT.NEW_DIRECT'),
    icon: 'i-lucide-message-circle',
  },
  {
    action: 'new_conversation',
    value: 'group',
    label: t('INTERNAL_CHAT.NEW_GROUP'),
    icon: 'i-lucide-users',
  },
]);

const roomFilterTabs = computed(() => [
  {
    value: 'all',
    label: t('INTERNAL_CHAT.FILTER_ALL'),
    count: roomsCountByKind.value.all,
  },
  {
    value: 'multi_user',
    label: t('INTERNAL_CHAT.FILTER_GROUPS'),
    count: roomsCountByKind.value.multi_user,
  },
  {
    value: 'direct',
    label: t('INTERNAL_CHAT.FILTER_DIRECT'),
    count: roomsCountByKind.value.direct,
  },
]);

const agentChoices = computed(() => {
  const selfId = Number(currentUser.value?.id);
  return (verifiedAgents.value || []).filter(a => Number(a.id) !== selfId);
});

function filterAgentsBySearch(list, queryRaw) {
  const q = queryRaw.trim().toLowerCase();
  if (!q) return list;
  return list.filter(a => {
    const name = (a.available_name || a.name || '').toLowerCase();
    const email = (a.email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  });
}

const filteredAgentsForDirect = computed(() =>
  filterAgentsBySearch(agentChoices.value || [], directAgentSearchQuery.value)
);

const filteredAgentsForGroup = computed(() =>
  filterAgentsBySearch(agentChoices.value || [], groupAgentSearchQuery.value)
);

const onlineAgents = computed(() =>
  (verifiedAgents.value || [])
    .filter(agent => agent?.availability_status === 'online')
    .sort((a, b) => {
      const na = (a.available_name || a.name || '').toLowerCase();
      const nb = (b.available_name || b.name || '').toLowerCase();
      return na.localeCompare(nb);
    })
);

const selectedRoom = computed(() =>
  rooms.value.find(r => String(r.id) === String(selectedRoomId.value))
);

const filteredRooms = computed(() => {
  const selectedKind = roomsFilterTab.value;
  if (!selectedKind || selectedKind === 'all') {
    return rooms.value;
  }

  return rooms.value.filter(room => room.kind === selectedKind);
});

let roomSearchDebounceTimeout = null;
let fetchRoomsRequestKey = 0;

function headerMembersForRoom(room) {
  if (!room) return [];
  if (Array.isArray(room.header_members) && room.header_members.length) {
    return room.header_members;
  }
  const selfId = currentUser.value?.id;
  if (Array.isArray(room.members)) {
    return room.members
      .filter(m => m.id !== selfId)
      .slice(0, 4)
      .map(m => ({
        name: m.available_name || m.name,
        avatar_url: m.thumbnail || m.avatar_url || '',
      }));
  }
  return [];
}

const selectedRoomHeaderMembers = computed(() =>
  headerMembersForRoom(selectedRoom.value)
);

const selectedRoomConversationAttachments = computed(() =>
  (messages.value || []).flatMap(msg =>
    (msg?.attachments || []).filter(att => Boolean(att?.url))
  )
);

const internalChatAgentsById = computed(() =>
  (verifiedAgents.value || []).reduce((acc, agent) => {
    acc[String(agent.id)] = agent;
    return acc;
  }, {})
);

function isCurrentUserInternalChatGroupAdmin(room) {
  if (!room || room.kind !== 'multi_user') return false;
  const myId = Number(currentUser.value?.id);
  if (Number(room.created_by_id) === myId) return true;
  const role = room.current_user_role;
  return role === 'admin' || role === 0 || role === '0';
}

const canEditSelectedGroupName = computed(() =>
  isCurrentUserInternalChatGroupAdmin(selectedRoom.value)
);

const selectedGroupAvatarUrl = computed(() => {
  if (selectedRoom.value?.kind !== 'multi_user') return '';
  const value = selectedRoom.value?.additional_data?.group_avatar_data_url;
  return typeof value === 'string' ? value : '';
});

function userIsSupervisorInFunnel(funnel, user) {
  if (!funnel || !user) return false;
  if (user.role === 'administrator') return false;
  const agents = funnel.settings?.agents;
  if (!Array.isArray(agents)) return false;
  return agents.some(agent => {
    if (Number(agent.id) !== Number(user.id)) return false;
    return Boolean(agent.is_supervisor || agent.supervisor);
  });
}

const assignFunnelOptions = computed(() =>
  (accountFunnels.value || []).filter(f => {
    if (f.active === false) return false;
    const u = currentUser.value;
    if (!u) return false;
    if (u.role === 'administrator') return true;
    return userIsSupervisorInFunnel(f, u);
  })
);

const directPeerUserId = computed(() => {
  const room = selectedRoom.value;
  if (!room || room.kind === 'multi_user') return null;
  const selfId = Number(currentUser.value?.id);
  const ids = (room.member_ids || []).map(Number).filter(n => n !== selfId);
  return ids[0] ?? null;
});

const canShowAssignFunnelCardButton = computed(
  () =>
    selectedRoom.value?.kind !== 'multi_user' &&
    directPeerUserId.value != null &&
    assignFunnelOptions.value.length > 0
);

const peerKanbanStageOptions = computed(() => {
  const fid = peerKanbanFunnelId.value;
  const funnel = (accountFunnels.value || []).find(
    f => String(f.id) === String(fid)
  );
  if (!funnel?.stages || typeof funnel.stages !== 'object') return [];
  return Object.entries(funnel.stages)
    .map(([id, st]) => ({
      id,
      name: (st && st.name) || id,
      position: Number.parseInt(st?.position, 10) || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

const peerKanbanFunnelSelectOptions = computed(() =>
  (assignFunnelOptions.value || []).map(f => ({
    value: String(f.id),
    label: f.name,
  }))
);

const assignFunnelSelectOptions = computed(() =>
  (assignFunnelOptions.value || []).map(f => ({
    value: String(f.id),
    label: f.name,
  }))
);

const peerKanbanStageSelectOptions = computed(() => [
  {
    value: '',
    label: INTERNAL_CHAT_PEER_KANBAN_PT.STAGE_ALL,
  },
  ...peerKanbanStageOptions.value.map(s => ({
    value: s.id,
    label: s.name,
  })),
]);

const manageGroupMembersSorted = computed(() => {
  const list = selectedRoom.value?.members;
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const na = (a.available_name || a.name || '').toLowerCase();
    const nb = (b.available_name || b.name || '').toLowerCase();
    return na.localeCompare(nb);
  });
});

const agentsAvailableToAddToGroup = computed(() => {
  const ids = new Set(
    (selectedRoom.value?.member_ids || []).map(id => Number(id))
  );
  const list = (verifiedAgents.value || []).filter(a => !ids.has(Number(a.id)));
  return filterAgentsBySearch(list, manageMembersAgentSearchQuery.value);
});

const INTERNAL_CHAT_GROUP_LIST_PALETTE = [
  'bg-n-ruby-9 text-white',
  'bg-n-teal-9 text-white',
  'bg-n-amber-9 text-white',
  'bg-n-iris-9 text-white',
  'bg-n-brand text-white',
  'bg-n-slate-9 text-white',
  'bg-n-yellow-9 text-n-slate-12',
  'bg-n-violet-9 text-white',
];

const INTERNAL_CHAT_FILE_PREVIEW_PALETTE = [
  'bg-n-brand text-white',
  'bg-n-teal-9 text-white',
  'bg-n-iris-9 text-white',
  'bg-n-slate-9 text-white',
  'bg-n-ruby-9 text-white',
  'bg-n-amber-9 text-white',
];

function stablePaletteIndex(seed, length) {
  const n = Number(seed);
  if (!Number.isNaN(n) && Number.isFinite(n)) {
    return Math.abs(Math.trunc(n)) % length;
  }
  const s = String(seed ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h) % length;
}

function groupRoomListColorClass(room) {
  const idx = stablePaletteIndex(
    room?.id,
    INTERNAL_CHAT_GROUP_LIST_PALETTE.length
  );
  return INTERNAL_CHAT_GROUP_LIST_PALETTE[idx];
}

function groupRoomListInitial(room) {
  const raw = (room?.name || room?.display_name || '').trim();
  if (!raw) return '?';
  const chars = [...raw];
  const letter = chars.find(c => /\p{L}/u.test(c)) ?? chars[0];
  return letter.toLocaleUpperCase(undefined);
}

function groupRoomListAvatarUrl(room) {
  if (room?.kind !== 'multi_user') return '';
  const value = room?.additional_data?.group_avatar_data_url;
  return typeof value === 'string' ? value : '';
}

function roomKindBadgeClass(room) {
  const base =
    'inline-flex flex-shrink-0 items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded-full border leading-none';
  if (room?.kind === 'multi_user') {
    return `${base} border-n-iris-9/25 bg-n-iris-9/10 text-n-iris-11`;
  }
  return `${base} border-n-teal-9/25 bg-n-teal-9/10 text-n-teal-11`;
}

const trimmedComposerPlainText = computed(() =>
  getPlainText(composerMessage.value || '').trim()
);

const hasComposerAttachments = computed(() => composerAttachments.value.length > 0);
const isComposerEmpty = computed(
  () => trimmedComposerPlainText.value === '' && !hasComposerAttachments.value
);

function normalizeMessage(raw) {
  return {
    id: raw.id,
    room_id: raw.room_id,
    content: raw.content,
    additional_data:
      raw.additional_data && typeof raw.additional_data === 'object'
        ? raw.additional_data
        : {},
    attachments: Array.isArray(raw.attachments) ? raw.attachments : [],
    created_at: raw.created_at,
    sender: raw.sender || {},
    read_by_all: Boolean(raw.read_by_all),
  };
}

function isOwnMessage(msg) {
  return Number(msg?.sender?.id) === Number(currentUser.value?.id);
}

function messageIsDeleted(msg) {
  return msg?.additional_data?.deleted === true;
}

function messageDisplayContent(msg) {
  if (!messageIsDeleted(msg)) return msg?.content || '';
  if (isOwnMessage(msg)) return msg?.additional_data?.deleted_content || '';
  return '';
}

function messageDeletedLabel(msg) {
  if (!messageIsDeleted(msg)) return '';
  return isOwnMessage(msg) ? 'Mensagem excluída por você' : 'Mensagem excluída';
}

function canManageMessage(msg) {
  return isOwnMessage(msg) && !messageIsDeleted(msg);
}

function messageIsPinned(msg) {
  return msg?.additional_data?.pinned === true;
}

function messagePinnedMode(msg) {
  const mode = String(msg?.additional_data?.pinned_mode || 'normal');
  return mode === 'announcement' ? 'announcement' : 'normal';
}

function messageIsAnnouncementPinned(msg) {
  return messageIsPinned(msg) && messagePinnedMode(msg) === 'announcement';
}

function messagePinnedExpiresAt(msg) {
  const raw = msg?.additional_data?.pinned_expires_at;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function messagePinExpired(msg) {
  const expiresAt = messagePinnedExpiresAt(msg);
  if (!expiresAt) return false;
  return expiresAt.getTime() <= Date.now();
}

function canUnpinMessage(msg) {
  if (!messageIsPinned(msg)) return true;
  if (!messageIsAnnouncementPinned(msg)) return true;
  if (selectedRoom.value?.kind !== 'multi_user') return true;
  return canEditSelectedGroupName.value || currentUser.value?.role === 'administrator';
}

function canPinAsAnnouncement() {
  return (
    selectedRoom.value?.kind === 'multi_user' &&
    (canEditSelectedGroupName.value || currentUser.value?.role === 'administrator')
  );
}

function canOpenContextMenuForMessage(msg) {
  if (!msg) return false;
  if (!messageIsDeleted(msg)) return true;
  return isOwnMessage(msg);
}

function messageCanReply(msg) {
  return Boolean(msg) && !messageIsDeleted(msg);
}

function findMessageById(messageId) {
  return messages.value.find(message => String(message.id) === String(messageId));
}

function replyPreviewText(msg) {
  if (!msg) return '';
  if (messageIsDeleted(msg)) return 'Mensagem excluída';
  const plain = getPlainText(String(messageDisplayContent(msg) || '')).trim();
  if (plain) return plain;
  if ((msg.attachments || []).length > 0) return 'Arquivo';
  return '';
}

function messageReplyTarget(msg) {
  const replyToId = msg?.additional_data?.reply_to_message_id;
  if (!replyToId) return null;
  return findMessageById(replyToId) || null;
}

function contextMenuMessage() {
  return messages.value.find(msg => msg.id === contextMessageId.value) || null;
}

const pinTargetMessage = computed(() =>
  messages.value.find(msg => msg.id === pinTargetMessageId.value) || null
);

const pinnedMessages = computed(() =>
  messages.value
    .filter(msg => messageIsPinned(msg) && !messagePinExpired(msg))
    .sort((a, b) => {
      const da = new Date(a?.additional_data?.pinned_at || a?.created_at || 0).getTime();
      const db = new Date(b?.additional_data?.pinned_at || b?.created_at || 0).getTime();
      return db - da;
    })
);

const pinnedMessagesCount = computed(() => pinnedMessages.value.length);

const currentPinnedMessage = computed(() => {
  if (pinnedMessages.value.length === 0) return null;
  const maxIndex = pinnedMessages.value.length - 1;
  const safeIndex = Math.min(Math.max(pinnedBannerIndex.value, 0), maxIndex);
  return pinnedMessages.value[safeIndex] || null;
});

const currentPinnedBannerPosition = computed(() =>
  pinnedMessages.value.length === 0 ? 0 : pinnedBannerIndex.value + 1
);

function pinnedMessagePreviewText(msg) {
  if (!msg) return '';
  const plain = getPlainText(String(messageDisplayContent(msg) || '')).trim();
  if (plain) return plain;
  if ((msg.attachments || []).length > 0) return 'Arquivo';
  return INTERNAL_CHAT_PINNED_MESSAGE_PT.BANNER_EMPTY;
}

async function toggleMessagePinned(msg) {
  return toggleMessagePinnedWithOptions(msg, {});
}

async function toggleMessagePinnedWithOptions(msg, options = {}) {
  if (!selectedRoomId.value || !msg?.id || messageIsDeleted(msg)) return;
  const nowPinned = messageIsPinned(msg);
  if (nowPinned && !canUnpinMessage(msg)) return;
  const mode = options.mode === 'announcement' ? 'announcement' : 'normal';
  try {
    const { data } = await internalChatRooms.updateMessageAdditionalData(
      selectedRoomId.value,
      msg.id,
      nowPinned
        ? { pinned: false }
        : {
            pinned: true,
            pinned_mode: mode,
          }
    );
    upsertMessageFromPayload(data);
  } catch {
    useAlert(INTERNAL_CHAT_PINNED_MESSAGE_PT.ERROR_PIN);
  }
}

function closeMessageContextMenu() {
  showMessageContextMenu.value = false;
  contextMessageId.value = null;
}

function closeReactionPicker() {
  reactionPickerMessageId.value = null;
}

function openMessageContextMenu(event, msg) {
  if (!canOpenContextMenuForMessage(msg)) return;
  event.preventDefault();
  contextMessageId.value = msg.id;
  messageContextMenuX.value = event.clientX;
  messageContextMenuY.value = event.clientY;
  showMessageContextMenu.value = true;
  nextTick(() => {
    const menu = messageContextMenuRef.value;
    if (!menu) return;
    const viewportPadding = 8;
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth - viewportPadding) {
      messageContextMenuX.value = Math.max(
        viewportPadding,
        window.innerWidth - rect.width - viewportPadding
      );
    }
    if (rect.bottom > window.innerHeight - viewportPadding) {
      messageContextMenuY.value = Math.max(
        viewportPadding,
        window.innerHeight - rect.height - viewportPadding
      );
    }
  });
}

function startEditMessage(msg) {
  editingMessageId.value = msg.id;
  editingMessageContent.value = messageDisplayContent(msg);
  editMessageDialogRef.value?.open();
}

function openDeleteMessageDialog(msg) {
  editingMessageId.value = msg.id;
  deleteMessageDialogRef.value?.open();
}

function handleEditFromContextMenu() {
  const msg = contextMenuMessage();
  closeMessageContextMenu();
  if (!msg) return;
  startEditMessage(msg);
}

function handleDeleteFromContextMenu() {
  const msg = contextMenuMessage();
  closeMessageContextMenu();
  if (!msg) return;
  openDeleteMessageDialog(msg);
}

function handleReplyFromContextMenu() {
  const msg = contextMenuMessage();
  closeMessageContextMenu();
  if (!messageCanReply(msg)) return;
  composerReplyToMessage.value = {
    id: msg.id,
    senderName: msg?.sender?.available_name || msg?.sender?.name || '',
    content: replyPreviewText(msg),
  };
}

function clearComposerReply() {
  composerReplyToMessage.value = null;
}

function openResendMessageDialog(msg) {
  resendingMessageId.value = msg.id;
  resendMessageDialogRef.value?.open();
}

function handleResendFromContextMenu() {
  const msg = contextMenuMessage();
  closeMessageContextMenu();
  if (!msg) return;
  openResendMessageDialog(msg);
}

function handleTogglePinFromContextMenu() {
  const msg = contextMenuMessage();
  closeMessageContextMenu();
  if (!msg || messageIsDeleted(msg)) return;
  if (messageIsPinned(msg)) {
    void toggleMessagePinnedWithOptions(msg, {});
    return;
  }
  pinTargetMessageId.value = msg.id;
  pinConfirmMode.value = 'normal';
  pinMessageDialogRef.value?.open();
}

async function handlePinMessageConfirm() {
  const msg = pinTargetMessage.value;
  if (!msg) return;
  if (pinConfirmMode.value === 'announcement' && !canPinAsAnnouncement()) {
    useAlert('Apenas administradores podem fixar como anuncio');
    return;
  }
  await toggleMessagePinnedWithOptions(msg, { mode: pinConfirmMode.value });
  pinMessageDialogRef.value?.close();
  pinTargetMessageId.value = null;
  pinConfirmMode.value = 'normal';
}

function scrollToMessage(messageId) {
  const container = messagesPaneRef.value;
  if (!container || !messageId) return;
  const el = container.querySelector(`[data-message-id="${messageId}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  highlightedMessageId.value = messageId;
  if (highlightedMessageTimeout) {
    clearTimeout(highlightedMessageTimeout);
  }
  highlightedMessageTimeout = window.setTimeout(() => {
    highlightedMessageId.value = null;
    highlightedMessageTimeout = null;
  }, 600);
}

function jumpToPinnedMessage() {
  const msg = currentPinnedMessage.value;
  if (!msg?.id) return;
  scrollToMessage(msg.id);
}

function jumpToReplyTarget(msg) {
  const target = messageReplyTarget(msg);
  if (!target?.id) return;
  scrollToMessage(target.id);
}

function goToNextPinnedMessage() {
  if (pinnedMessages.value.length <= 1) return;
  pinnedBannerIndex.value =
    (pinnedBannerIndex.value + 1) % pinnedMessages.value.length;
}

function goToPreviousPinnedMessage() {
  if (pinnedMessages.value.length <= 1) return;
  pinnedBannerIndex.value =
    (pinnedBannerIndex.value - 1 + pinnedMessages.value.length) %
    pinnedMessages.value.length;
}

async function handleResendMessageConfirm() {
  if (!selectedRoomId.value || !resendingMessageId.value || resendingMessage.value) return;
  const msg = messages.value.find(m => m.id === resendingMessageId.value);
  if (!msg) return;
  const content = String(msg?.additional_data?.deleted_content || '').trim();
  if (!content) {
    useAlert('Nao ha conteudo para reenviar nesta mensagem');
    return;
  }

  resendingMessage.value = true;
  try {
    const { data } = await internalChatRooms.sendMessage(
      selectedRoomId.value,
      content,
      []
    );
    upsertMessageFromPayload(data);
    bumpRoomFromPayload(data);
    resendMessageDialogRef.value?.close();
    resendingMessageId.value = null;
    await scrollMessagesToEnd();
  } catch {
    useAlert('Nao foi possivel reenviar a mensagem');
  } finally {
    resendingMessage.value = false;
  }
}

function upsertMessageFromPayload(payload) {
  const normalized = normalizeMessage(payload);
  const index = messages.value.findIndex(msg => msg.id === normalized.id);
  if (index === -1) {
    messages.value.push(normalized);
    return;
  }
  messages.value.splice(index, 1, normalized);
}

function sortRooms(list) {
  return [...list].sort((a, b) => {
    const da = new Date(a.last_message_at || 0).getTime();
    const db = new Date(b.last_message_at || 0).getTime();
    return db - da;
  });
}

function lastMessagePreviewFromPayload(payload) {
  if (payload == null || payload.id == null) return null;
  if (payload.content === undefined || payload.content === null) return null;
  const sender = payload.sender || {};
  return {
    id: payload.id,
    content:
      typeof payload.content === 'string'
        ? payload.content
        : String(payload.content),
    sender_id: sender.id ?? payload.sender_id,
    sender_name: sender.available_name ?? sender.name ?? payload.sender_name,
    attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
    created_at: payload.created_at,
  };
}

function bumpRoomFromPayload(payload) {
  const rid = payload.room_id ?? payload.internal_chat_room?.id;
  if (!rid) return;
  const ts = payload.created_at;
  const room = rooms.value.find(r => r.id === rid);
  if (!room) return;
  if (ts) room.last_message_at = ts;
  const preview = lastMessagePreviewFromPayload(payload);
  if (preview) room.last_message = preview;
  rooms.value = sortRooms(rooms.value);
}

function roomUnreadCount(room) {
  const value = Number(room?.unread_count || 0);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.trunc(value);
}

function roomLastMessagePreview(room) {
  const lm = room.last_message;
  if (!lm || lm.content == null) return '';
  const plain = getPlainText(String(lm.content)).trim();
  const hasImageAttachment = (lm.attachments || []).some(att =>
    String(att?.content_type || '').startsWith('image/')
  );
  const baseText = plain || (hasImageAttachment ? t('INTERNAL_CHAT.LAST_IMAGE') : '');
  if (!baseText) return '';
  const selfId = currentUser.value?.id;
  const fromYou =
    lm.sender_id != null && Number(lm.sender_id) === Number(selfId);
  if (fromYou) {
    return `${t('INTERNAL_CHAT.LAST_FROM_YOU')}: ${baseText}`;
  }
  if (room.kind === 'multi_user' && lm.sender_name) {
    return `${lm.sender_name}: ${baseText}`;
  }
  return baseText;
}

function roomLastMessageHasImageAttachment(room) {
  const attachments = room?.last_message?.attachments || [];
  return attachments.some(att =>
    String(att?.content_type || '').startsWith('image/')
  );
}

function applyRoomDetailFromShow(data) {
  if (!data?.id) return;
  rooms.value = rooms.value.map(r =>
    r.id === data.id
      ? {
          ...r,
          members: data.members,
          member_ids: data.member_ids,
          header_members: data.header_members,
          display_name: data.display_name,
          name: data.name,
          additional_data: data.additional_data || {},
          current_user_role: data.current_user_role,
          created_by_id: data.created_by_id,
        }
      : r
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('failed_to_read_file'));
    reader.readAsDataURL(file);
  });
}

async function fetchRooms() {
  const requestKey = fetchRoomsRequestKey + 1;
  fetchRoomsRequestKey = requestKey;
  loadingRooms.value = true;
  const query = roomSearchQuery.value.trim();
  const selectedKind = roomsFilterTab.value;
  try {
    const { data } = await internalChatRooms.get({
      ...(query ? { q: query } : {}),
      ...(selectedKind && selectedKind !== 'all' ? { kind: selectedKind } : {}),
    });
    if (requestKey !== fetchRoomsRequestKey) return;
    const payload = Array.isArray(data) ? { rooms: data } : data || {};
    rooms.value = sortRooms(Array.isArray(payload.rooms) ? payload.rooms : []);
    const counts = payload.counts || {};
    roomsCountByKind.value = {
      all: Number(counts.all || 0),
      direct: Number(counts.direct || 0),
      multi_user: Number(counts.multi_user || 0),
    };
  } catch {
    if (requestKey !== fetchRoomsRequestKey) return;
    useAlert(t('INTERNAL_CHAT.ERROR_LOAD'));
  } finally {
    if (requestKey !== fetchRoomsRequestKey) return;
    loadingRooms.value = false;
  }
}

async function refreshSelectedRoomDetail() {
  const roomId = selectedRoomId.value;
  if (!roomId) return;
  const { data } = await internalChatRooms.show(roomId);
  applyRoomDetailFromShow(data);
}

async function loadMessages(roomId) {
  loadingMessages.value = true;
  messages.value = [];
  try {
    const { data } = await internalChatRooms.messages(roomId);
    const list = Array.isArray(data) ? data : [];
    messages.value = list.map(normalizeMessage);
  } catch {
    useAlert(t('INTERNAL_CHAT.ERROR_LOAD'));
  } finally {
    loadingMessages.value = false;
    await scrollMessagesToEnd();
  }
}

async function selectRoom(roomId) {
  selectedRoomId.value = roomId;
  await loadMessages(roomId);
  try {
    await internalChatRooms.markRead(roomId);
    const room = rooms.value.find(r => String(r.id) === String(roomId));
    if (room) room.unread_count = 0;
  } catch {
    /* ignore */
  }
  await focusComposerInput();
}

async function scrollMessagesToEnd() {
  await nextTick();
  const el = messagesPaneRef.value;
  if (el) {
    el.scrollTop = el.scrollHeight;
  }
}

async function focusComposerInput() {
  await nextTick();
  composerEditorRef.value?.focusEditorInputField?.();
}

async function sendMessage() {
  const text = trimmedComposerPlainText.value;
  if ((!text && !hasComposerAttachments.value) || !selectedRoomId.value || sending.value) return;
  sending.value = true;
  const replyData = composerReplyToMessage.value
    ? { reply_to_message_id: composerReplyToMessage.value.id }
    : null;
  try {
    const { data } = await internalChatRooms.sendMessage(
      selectedRoomId.value,
      text,
      composerAttachments.value,
      replyData
    );
    const msg = normalizeMessage(data);
    if (!messages.value.some(m => m.id === msg.id)) {
      messages.value.push(msg);
    }
    composerMessage.value = '';
    composerAttachments.value = [];
    composerReplyToMessage.value = null;
    bumpRoomFromPayload(msg);
    await scrollMessagesToEnd();
  } catch {
    useAlert(t('INTERNAL_CHAT.ERROR_SEND'));
  } finally {
    sending.value = false;
    await focusComposerInput();
  }
}

function onComposerAttachmentSelected(event) {
  const files = Array.from(event.target?.files || []);
  if (files.length === 0) return;
  composerAttachments.value = [...composerAttachments.value, ...files];
  event.target.value = '';
}

function removeComposerAttachment(index) {
  composerAttachments.value = composerAttachments.value.filter((_, i) => i !== index);
}

function openComposerAttachmentPicker() {
  composerAttachmentInputRef.value?.click();
}

function onRealtimeMessage(payload) {
  const rid = payload.room_id ?? payload.internal_chat_room?.id;
  if (!rid || !payload.id) return;
  const senderId = Number(payload?.sender?.id);
  const currentUserId = Number(currentUser.value?.id);

  if (selectedRoomId.value === rid) {
    const exists = messages.value.some(m => m.id === payload.id);
    if (!exists) {
      messages.value.push(normalizeMessage(payload));
      scrollMessagesToEnd();
    }
  } else if (senderId && senderId !== currentUserId) {
    const room = rooms.value.find(r => String(r.id) === String(rid));
    if (room) room.unread_count = roomUnreadCount(room) + 1;
  }
  bumpRoomFromPayload(payload);
}

function onRealtimeMessageUpdated(payload) {
  const rid = payload.room_id ?? payload.internal_chat_room?.id;
  if (!rid || !payload.id) return;
  if (selectedRoomId.value === rid) {
    upsertMessageFromPayload(payload);
  }
  bumpRoomFromPayload(payload);
}

async function handleEditMessageConfirm() {
  const roomId = selectedRoomId.value;
  const messageId = editingMessageId.value;
  const content = editingMessageContent.value.trim();
  if (!roomId || !messageId || !content || savingMessageEdit.value) return;

  savingMessageEdit.value = true;
  try {
    const { data } = await internalChatRooms.updateMessage(roomId, messageId, content);
    upsertMessageFromPayload(data);
    editMessageDialogRef.value?.close();
    editingMessageId.value = null;
    editingMessageContent.value = '';
  } catch {
    useAlert('Nao foi possivel editar a mensagem');
  } finally {
    savingMessageEdit.value = false;
  }
}

async function handleDeleteMessageConfirm() {
  const roomId = selectedRoomId.value;
  const messageId = editingMessageId.value;
  if (!roomId || !messageId || deletingMessage.value) return;

  deletingMessage.value = true;
  try {
    const { data } = await internalChatRooms.deleteMessage(roomId, messageId);
    upsertMessageFromPayload(data);
    deleteMessageDialogRef.value?.close();
    editingMessageId.value = null;
  } catch {
    useAlert('Nao foi possivel excluir a mensagem');
  } finally {
    deletingMessage.value = false;
  }
}

async function handleDirectConfirm() {
  if (!directUserId.value) return;
  await createOrSelectDirectRoom(Number(directUserId.value));
  directDialogRef.value?.close();
  directUserId.value = '';
  directAgentSearchQuery.value = '';
}

async function createOrSelectDirectRoom(userId) {
  if (!userId || creatingDirect.value) return;
  creatingDirect.value = true;
  try {
    const { data } = await internalChatRooms.create({
      room: { kind: 'direct', user_id: Number(userId) },
    });
    await fetchRooms();
    await selectRoom(data.id);
  } catch {
    useAlert(t('INTERNAL_CHAT.ERROR_CREATE'));
  } finally {
    creatingDirect.value = false;
  }
}

async function handleOnlineAgentAvatarClick(agent) {
  const agentId = Number(agent?.id);
  if (!agentId) return;
  if (agentId === Number(currentUser.value?.id)) return;
  await createOrSelectDirectRoom(agentId);
}

async function handleGroupConfirm() {
  const name = groupName.value.trim();
  if (!name || groupMemberIds.value.length === 0) return;
  creatingGroup.value = true;
  try {
    const { data } = await internalChatRooms.create({
      room: {
        kind: 'multi_user',
        name,
        user_ids: groupMemberIds.value.map(Number),
      },
    });
    groupDialogRef.value?.close();
    groupName.value = '';
    groupMemberIds.value = [];
    groupAgentSearchQuery.value = '';
    await fetchRooms();
    await selectRoom(data.id);
  } catch {
    useAlert(t('INTERNAL_CHAT.ERROR_CREATE'));
  } finally {
    creatingGroup.value = false;
  }
}

function openDirectDialog() {
  directUserId.value = '';
  directAgentSearchQuery.value = '';
  directDialogRef.value?.open();
}

function openGroupDialog() {
  groupName.value = '';
  groupMemberIds.value = [];
  groupAgentSearchQuery.value = '';
  groupDialogRef.value?.open();
}

function openLeaveGroupDialog() {
  leaveGroupDialogRef.value?.open();
}

function openEditGroupNameDialog() {
  const r = selectedRoom.value;
  editGroupName.value = (r?.name || r?.display_name || '').trim();
  editGroupNameDialogRef.value?.open();
}

async function openManageMembersDialog() {
  const roomId = selectedRoomId.value;
  if (!roomId) return;
  manageMembersAgentSearchQuery.value = '';
  loadingManageMembers.value = true;
  try {
    const { data } = await internalChatRooms.show(roomId);
    applyRoomDetailFromShow(data);
    manageMembersDialogRef.value?.open();
  } catch {
    useAlert(INTERNAL_CHAT_MANAGE_MEMBERS_PT.ERROR_LOAD);
  } finally {
    loadingManageMembers.value = false;
  }
}

async function handleAddMemberToGroup(userId) {
  const roomId = selectedRoomId.value;
  if (!roomId) return;
  memberActionLoadingId.value = userId;
  try {
    await internalChatRooms.addMember(roomId, userId);
    await refreshSelectedRoomDetail();
  } catch {
    useAlert(INTERNAL_CHAT_MANAGE_MEMBERS_PT.ERROR_ADD);
  } finally {
    memberActionLoadingId.value = null;
  }
}

async function handleRemoveMemberFromGroup(userId) {
  const roomId = selectedRoomId.value;
  if (!roomId) return;
  memberActionLoadingId.value = userId;
  try {
    await internalChatRooms.removeMember(roomId, userId);
    if (Number(userId) === Number(currentUser.value?.id)) {
      manageMembersDialogRef.value?.close();
      rooms.value = rooms.value.filter(r => r.id !== roomId);
      selectedRoomId.value = null;
      messages.value = [];
      return;
    }
    await refreshSelectedRoomDetail();
  } catch {
    useAlert(INTERNAL_CHAT_MANAGE_MEMBERS_PT.ERROR_REMOVE);
  } finally {
    memberActionLoadingId.value = null;
  }
}

async function handleEditGroupNameConfirm() {
  const name = editGroupName.value.trim();
  const roomId = selectedRoomId.value;
  if (!name || !roomId) return;
  savingGroupName.value = true;
  try {
    const { data } = await internalChatRooms.update(roomId, {
      room: { name },
    });
    applyRoomDetailFromShow(data);
    editGroupNameDialogRef.value?.close();
  } catch {
    useAlert(INTERNAL_CHAT_EDIT_GROUP_NAME_PT.ERROR);
  } finally {
    savingGroupName.value = false;
  }
}

async function handleGroupAvatarUpdate(file) {
  const roomId = selectedRoomId.value;
  if (!roomId || savingGroupAvatar.value) return;
  if (!file || !String(file.type || '').startsWith('image/')) {
    useAlert('Selecione um arquivo de imagem valido');
    return;
  }

  savingGroupAvatar.value = true;
  try {
    const avatarDataUrl = await fileToDataUrl(file);
    const currentData = selectedRoom.value?.additional_data || {};
    const { data } = await internalChatRooms.update(roomId, {
      room: {
        additional_data: {
          ...currentData,
          group_avatar_data_url: avatarDataUrl,
        },
      },
    });
    applyRoomDetailFromShow(data);
  } catch {
    useAlert('Nao foi possivel salvar a foto do grupo');
  } finally {
    savingGroupAvatar.value = false;
  }
}

async function handleGroupAvatarClear() {
  const roomId = selectedRoomId.value;
  if (!roomId || savingGroupAvatar.value) return;
  savingGroupAvatar.value = true;
  try {
    const currentData = selectedRoom.value?.additional_data || {};
    const { data } = await internalChatRooms.update(roomId, {
      room: {
        additional_data: {
          ...currentData,
          group_avatar_data_url: null,
        },
      },
    });
    applyRoomDetailFromShow(data);
  } catch {
    useAlert('Nao foi possivel remover a foto do grupo');
  } finally {
    savingGroupAvatar.value = false;
  }
}

async function loadAccountFunnelsForAssign() {
  try {
    const { data } = await FunnelAPI.get();
    accountFunnels.value = Array.isArray(data) ? data : [];
  } catch {
    accountFunnels.value = [];
  }
}

async function fetchAssignKanbanItems() {
  const fid = assignSelectedFunnelId.value;
  if (!fid) {
    assignKanbanItems.value = [];
    return;
  }
  assignLoadingItems.value = true;
  try {
    const response = await KanbanAPI.getItems(fid);
    const items = response.data?.items || [];
    assignKanbanItems.value = Array.isArray(items) ? items : [];
  } catch {
    assignKanbanItems.value = [];
    useAlert(INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.ERROR_LOAD_ITEMS);
  } finally {
    assignLoadingItems.value = false;
  }
}

async function fetchPeerKanbanItems() {
  const fid = peerKanbanFunnelId.value;
  const agentId = directPeerUserId.value;
  if (!fid || agentId == null) {
    peerKanbanItems.value = [];
    return;
  }
  peerKanbanLoading.value = true;
  try {
    const stageKey = peerKanbanStageId.value.trim();
    const stageParam = stageKey === '' ? undefined : stageKey;
    const response = await KanbanAPI.getItems(
      fid,
      1,
      stageParam,
      agentId
    );
    const items = response.data?.items || [];
    peerKanbanItems.value = Array.isArray(items) ? items : [];
  } catch {
    peerKanbanItems.value = [];
    useAlert(INTERNAL_CHAT_PEER_KANBAN_PT.ERROR_LOAD);
  } finally {
    peerKanbanLoading.value = false;
  }
}

function openPeerKanbanPanel() {
  showAssignFunnelPanel.value = false;
  showConversationDetailsPanel.value = false;
  const opts = assignFunnelOptions.value;
  peerKanbanFunnelId.value = opts[0] ? String(opts[0].id) : '';
  peerKanbanStageId.value = '';
  showPeerKanbanPanel.value = true;
  void fetchPeerKanbanItems();
}

function closePeerKanbanPanel() {
  showPeerKanbanPanel.value = false;
}

function closeAssignFunnelPanel() {
  showAssignFunnelPanel.value = false;
}

function closeInternalChatSidePanels() {
  showPeerKanbanPanel.value = false;
  showAssignFunnelPanel.value = false;
  showConversationDetailsPanel.value = false;
}

function openAssignFunnelPanel() {
  showPeerKanbanPanel.value = false;
  showConversationDetailsPanel.value = false;
  const opts = assignFunnelOptions.value;
  assignSelectedFunnelId.value = opts[0] ? String(opts[0].id) : '';
  assignSelectedItemIds.value = [];
  assignMode.value = 'add';
  showAssignFunnelPanel.value = true;
  fetchAssignKanbanItems();
}

function peerKanbanItemAriaLabel(item) {
  const meta = peerKanbanItemListMeta(item);
  const parts = [meta.primaryText];
  if (meta.funnelName) parts.push(meta.funnelName);
  if (meta.stage?.name) parts.push(meta.stage.name);
  return `${INTERNAL_CHAT_PEER_KANBAN_PT.OPEN_DETAIL_ARIA}: ${parts.join(', ')}`;
}

function openPeerKanbanItemDetail(item) {
  if (!item?.id) return;
  router.push(accountScopedRoute('kanban_board', {}, { item: item.id }));
  closePeerKanbanPanel();
}

async function openConversationDetailsPanel() {
  showPeerKanbanPanel.value = false;
  showAssignFunnelPanel.value = false;
  try {
    await refreshSelectedRoomDetail();
  } catch {
    /* ignore: painel ainda abre com dados já disponíveis */
  }
  showConversationDetailsPanel.value = true;
}

function toggleAssignItem(itemId) {
  const i = assignSelectedItemIds.value.indexOf(itemId);
  if (i === -1) {
    assignSelectedItemIds.value = [...assignSelectedItemIds.value, itemId];
  } else {
    assignSelectedItemIds.value = assignSelectedItemIds.value.filter(
      id => id !== itemId
    );
  }
}

async function handleAssignFunnelCardsConfirm() {
  const agentId = directPeerUserId.value;
  const ids = assignSelectedItemIds.value;
  if (!agentId || ids.length === 0 || assignSubmitting.value) return;
  assignSubmitting.value = true;
  try {
    await KanbanAPI.bulkAssignAgent(ids, agentId, assignMode.value);
    useAlert(INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.SUCCESS);
    closeAssignFunnelPanel();
  } catch {
    useAlert(INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.ERROR_ASSIGN);
  } finally {
    assignSubmitting.value = false;
  }
}

function assignItemRowParts(item) {
  const id = item?.id;
  const idPart = id != null && id !== '' ? `#${id}` : '';
  const rawTitle =
    item?.item_details?.title || item?.title || item?.name || '';
  const title = String(rawTitle).trim() || 'Sem título';
  const primaryText = idPart ? `${idPart} — ${title}` : title;

  const stageKey = item?.funnel_stage;
  let stage = null;
  if (stageKey) {
    const st = item?.funnel?.stages?.[stageKey];
    stage = {
      name: (st && st.name) || stageKey,
      color: (st && st.color) || '#64748b',
    };
  }

  return { primaryText, stage, title, idPart };
}

function peerKanbanItemListMeta(item) {
  const base = assignItemRowParts(item);
  const details = item?.item_details || {};
  const priorityRaw = String(details.priority || 'none').toLowerCase();
  const showPriority =
    priorityRaw !== 'none' &&
    priorityRaw !== '' &&
    ['low', 'medium', 'high', 'urgent'].includes(priorityRaw);

  const desc = String(details.description || '').trim();
  const agents = Array.isArray(item?.assigned_agents)
    ? item.assigned_agents
    : [];
  const maxAvatars = 4;

  return {
    ...base,
    funnelName: item?.funnel?.name || '',
    conversationDisplayId: item?.conversation_display_id,
    priority: showPriority ? priorityRaw : 'none',
    showPriority,
    description: desc.length ? desc : '',
    assignedAgents: agents.slice(0, maxAvatars),
    assignedExtraCount: Math.max(0, agents.length - maxAvatars),
    updatedRel: formatRelativeListTime(item?.updated_at),
    stageEnteredRel: formatRelativeListTime(item?.stage_entered_at),
    attachmentsCount:
      typeof item?.attachments?.total_count === 'number'
        ? item.attachments.total_count
        : 0,
    checklistOpenCount: Array.isArray(item?.checklist)
      ? item.checklist.filter(
          entry => !entry?.completed && !entry?.done
        ).length
      : 0,
    checklistTotalCount: Array.isArray(item?.checklist)
      ? item.checklist.length
      : 0,
  };
}

watch(assignSelectedFunnelId, () => {
  assignSelectedItemIds.value = [];
  fetchAssignKanbanItems();
});

function onPeerKanbanFunnelChange() {
  peerKanbanStageId.value = '';
  fetchPeerKanbanItems();
}

function onPeerKanbanFunnelSelectUpdate(value) {
  peerKanbanFunnelId.value = value;
  onPeerKanbanFunnelChange();
}

async function handleLeaveGroupConfirm() {
  const roomId = selectedRoomId.value;
  const uid = currentUser.value?.id;
  if (!roomId || uid == null) return;
  leavingGroup.value = true;
  try {
    await internalChatRooms.removeMember(roomId, uid);
    leaveGroupDialogRef.value?.close();
    rooms.value = rooms.value.filter(r => r.id !== roomId);
    selectedRoomId.value = null;
    messages.value = [];
  } catch {
    useAlert(t('INTERNAL_CHAT.ERROR_LEAVE'));
  } finally {
    leavingGroup.value = false;
  }
}

function onNewConversationMenuAction({ value }) {
  showNewConversationMenu.value = false;
  if (value === 'direct') openDirectDialog();
  else if (value === 'group') openGroupDialog();
}

function toggleGroupMember(id) {
  const i = groupMemberIds.value.indexOf(id);
  if (i === -1) {
    groupMemberIds.value = [...groupMemberIds.value, id];
  } else {
    groupMemberIds.value = groupMemberIds.value.filter(x => x !== id);
  }
}

function isGroupMemberChecked(id) {
  return groupMemberIds.value.includes(id);
}

function formatRelativeListTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '';
  }
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isToday(d)) {
      return d.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function messageReadCheckIconClass(msg) {
  return msg?.read_by_all ? 'i-lucide-check-check' : 'i-lucide-check';
}

function messageReactionsMap(msg) {
  const reactions = msg?.additional_data?.reactions;
  return reactions && typeof reactions === 'object' ? reactions : {};
}

function messageHasCurrentUserReaction(msg) {
  const uid = String(currentUser.value?.id || '');
  if (!uid) return false;
  return Boolean(messageReactionsMap(msg)[uid]);
}

function currentUserReactionEmoji(msg) {
  const uid = String(currentUser.value?.id || '');
  if (!uid) return '';
  return String(messageReactionsMap(msg)[uid] || '');
}

function messageReactionSummary(msg) {
  const reactions = messageReactionsMap(msg);
  const uid = String(currentUser.value?.id || '');
  const grouped = Object.values(reactions).reduce((acc, emoji) => {
    const key = String(emoji || '').trim();
    if (!key) return acc;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const ownReaction = uid ? reactions[uid] : null;
  return Object.entries(grouped).map(([emoji, count]) => ({
    emoji,
    count,
    isOwnReaction: ownReaction === emoji,
  }));
}

function messageReactionBadgeEmojis(msg) {
  return messageReactionSummary(msg).map(item => item.emoji);
}

function messageReactionTotalCount(msg) {
  return messageReactionSummary(msg).reduce(
    (total, item) => total + item.count,
    0
  );
}

function toggleReactionPicker(msg) {
  if (reactionPickerMessageId.value === msg.id) {
    reactionPickerMessageId.value = null;
    return;
  }
  reactionPickerMessageId.value = msg.id;
}

async function selectMessageReaction(msg, emoji) {
  if (!selectedRoomId.value || !msg?.id || messageIsDeleted(msg)) return;
  const uid = String(currentUser.value?.id || '');
  if (!uid) return;

  const reactions = { ...messageReactionsMap(msg) };
  if (reactions[uid] === emoji) delete reactions[uid];
  else reactions[uid] = emoji;

  try {
    const { data } = await internalChatRooms.updateMessageAdditionalData(
      selectedRoomId.value,
      msg.id,
      { reactions }
    );
    upsertMessageFromPayload(data);
    closeReactionPicker();
  } catch {
    useAlert('Nao foi possivel reagir a mensagem');
  }
}

function parseMessageDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function messageDateBadgeLabel(iso) {
  const date = parseMessageDate(iso);
  if (!date) return '';
  if (isToday(date)) return t('INTERNAL_CHAT.DATE_BADGE_TODAY');
  if (isYesterday(date)) return t('INTERNAL_CHAT.DATE_BADGE_YESTERDAY');
  if (isSameDay(date, subDays(new Date(), 2))) {
    return t('INTERNAL_CHAT.DATE_BADGE_DAY_BEFORE_YESTERDAY');
  }
  return date.toLocaleDateString('pt-BR');
}

function shouldShowDateBadge(msg, index) {
  if (!msg?.created_at) return false;
  if (index === 0) return true;
  const prev = messages.value[index - 1];
  if (!prev?.created_at) return true;
  const currentDate = parseMessageDate(msg.created_at);
  const prevDate = parseMessageDate(prev.created_at);
  if (!currentDate || !prevDate) return true;
  return !isSameDay(currentDate, prevDate);
}

function formatAttachmentSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExtensionLabel(name) {
  const raw = String(name || '').trim().toLowerCase();
  if (!raw.includes('.')) return 'FILE';
  const ext = raw.split('.').pop();
  if (!ext) return 'FILE';
  return ext.slice(0, 4).toUpperCase();
}

function attachmentExtensionLabel(attachment) {
  return fileExtensionLabel(attachment?.filename || attachment?.file_type);
}

function extensionPreviewColorClass(extensionLabel) {
  let hash = 0;
  for (let i = 0; i < extensionLabel.length; i += 1) {
    hash = (hash << 5) - hash + extensionLabel.charCodeAt(i);
    hash |= 0;
  }
  return INTERNAL_CHAT_FILE_PREVIEW_PALETTE[
    Math.abs(hash) % INTERNAL_CHAT_FILE_PREVIEW_PALETTE.length
  ];
}

watch(
  () => messages.value.length,
  () => {
    scrollMessagesToEnd();
  }
);

watch(pinnedMessages, newPinnedMessages => {
  if (newPinnedMessages.length === 0) {
    pinnedBannerIndex.value = 0;
    return;
  }
  if (pinnedBannerIndex.value > newPinnedMessages.length - 1) {
    pinnedBannerIndex.value = 0;
  }
});

watch(selectedRoomId, () => {
  composerMessage.value = '';
  composerAttachments.value = [];
  composerReplyToMessage.value = null;
  closeInternalChatSidePanels();
});

watch(roomSearchQuery, () => {
  if (roomSearchDebounceTimeout) clearTimeout(roomSearchDebounceTimeout);
  roomSearchDebounceTimeout = setTimeout(() => {
    fetchRooms();
  }, 300);
});

watch(roomsFilterTab, () => {
  fetchRooms();
});

onMounted(async () => {
  if (!isStacklab) return;

  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('kanban/fetchKanbanConfig'),
  ]);
  await loadAccountFunnelsForAssign();
  await fetchRooms();
  emitter.on(BUS_EVENTS.INTERNAL_CHAT_MESSAGE_CREATED, onRealtimeMessage);
  emitter.on(BUS_EVENTS.INTERNAL_CHAT_MESSAGE_UPDATED, onRealtimeMessageUpdated);
  window.addEventListener('click', closeMessageContextMenu);
  window.addEventListener('click', closeReactionPicker);
});

onUnmounted(() => {
  if (roomSearchDebounceTimeout) {
    clearTimeout(roomSearchDebounceTimeout);
    roomSearchDebounceTimeout = null;
  }
  if (highlightedMessageTimeout) {
    clearTimeout(highlightedMessageTimeout);
    highlightedMessageTimeout = null;
  }
  emitter.off(BUS_EVENTS.INTERNAL_CHAT_MESSAGE_CREATED, onRealtimeMessage);
  emitter.off(BUS_EVENTS.INTERNAL_CHAT_MESSAGE_UPDATED, onRealtimeMessageUpdated);
  window.removeEventListener('click', closeMessageContextMenu);
  window.removeEventListener('click', closeReactionPicker);
});
</script>

<template>
  <div
    v-if="!isStacklab"
    class="w-full h-full bg-slate-50 dark:bg-slate-900 flex items-center justify-center"
  >
    <div class="text-center max-w-md mx-auto p-6">
      <div class="text-red-500 text-6xl mb-4">🔒</div>
      <h2
        class="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2"
      >
        Licença Necessária
      </h2>
      <p class="text-slate-600 dark:text-slate-400">
        Esta instalação não possui uma licença válida para acessar o Kanban.
        Entre em contato com o administrador da plataforma.
      </p>
    </div>
  </div>
  <div v-else class="flex w-full h-full min-h-0 bg-n-background text-n-slate-12">
    <aside
      class="flex flex-col w-[280px] min-w-[240px] max-w-[320px] border-r border-n-weak shrink-0"
    >
      <div class="flex flex-col gap-2 px-3 pt-3 pb-0">
        <div class="flex gap-2 justify-between items-center">
          <div class="text-sm font-medium text-n-slate-11">
            {{ t('INTERNAL_CHAT.TITLE') }}
          </div>
          <div
            v-on-click-outside="() => (showNewConversationMenu = false)"
            class="relative flex-shrink-0"
          >
            <Button
              icon="i-lucide-plus"
              size="sm"
              color="blue"
              variant="solid"
              class="rounded-lg"
              :title="t('INTERNAL_CHAT.ADD_CONVERSATION')"
              :aria-label="t('INTERNAL_CHAT.ADD_CONVERSATION')"
              @click="showNewConversationMenu = !showNewConversationMenu"
            />
            <DropdownMenu
              v-if="showNewConversationMenu"
              :menu-items="newConversationMenuItems"
              class="z-[100] overflow-y-auto top-full mt-1 min-w-[200px] ltr:right-0 rtl:left-0"
              @action="onNewConversationMenuAction"
            />
          </div>
        </div>
        <div
          v-if="onlineAgents.length"
          class="flex flex-col gap-2"
        >
          <div class="flex justify-between items-center">
            <span class="text-xs font-semibold text-n-slate-12">
              Online agora
            </span>
            <span class="text-xxs font-medium text-n-slate-12 tabular-nums">
              {{ onlineAgents.length }}
            </span>
          </div>
          <div
            class="flex items-center gap-2 overflow-x-auto overflow-y-visible py-0.5 no-scrollbar"
          >
            <button
              v-for="agent in onlineAgents"
              :key="agent.id"
              type="button"
              class="relative flex-shrink-0 ring-2 ring-n-background rounded-full p-0 border-0 bg-transparent disabled:opacity-60 disabled:cursor-not-allowed"
              :title="agent.available_name || agent.name"
              :aria-label="`Conversar com ${agent.available_name || agent.name}`"
              :disabled="creatingDirect || Number(agent.id) === Number(currentUser?.id)"
              @click="handleOnlineAgentAvatarClick(agent)"
            >
              <Avatar
                :name="agent.available_name || agent.name"
                :src="agent.thumbnail || agent.avatar_url || ''"
                :size="28"
                rounded-full
              />
              <span
                class="absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full bg-n-teal-9 ring-2 ring-n-background"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div
          class="grid grid-cols-3 gap-1 p-1 w-full rounded-xl border border-n-weak bg-n-alpha-2"
        >
          <button
            v-for="tab in roomFilterTabs"
            :key="tab.value"
            type="button"
            class="inline-flex gap-1 justify-center items-center w-full px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors"
            :class="
              roomsFilterTab === tab.value
                ? 'border-n-brand bg-n-background text-n-brand shadow-sm'
                : 'border-transparent bg-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="roomsFilterTab = tab.value"
          >
            <span class="truncate">{{ tab.label }}</span>
            <span
              class="inline-flex justify-center items-center px-1.5 min-w-[1.2rem] h-4 text-xxs rounded-full"
              :class="
                roomsFilterTab === tab.value
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'bg-n-alpha-3 text-n-slate-10'
              "
            >
              {{ tab.count }}
            </span>
          </button>
        </div>
        <label class="sr-only" for="internal-chat-room-search">
          {{ t('INTERNAL_CHAT.SEARCH_CONVERSATIONS') }}
        </label>
        <input
          id="internal-chat-room-search"
          v-model="roomSearchQuery"
          type="text"
          class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
          :placeholder="t('INTERNAL_CHAT.SEARCH_CONVERSATIONS')"
        />
      </div>
      <div class="overflow-y-auto no-scrollbar flex-1 min-h-0">
        <div v-if="loadingRooms" class="p-4 text-sm text-n-slate-11">
          {{ t('INTERNAL_CHAT.LOADING') }}
        </div>
        <div
          v-else-if="filteredRooms.length === 0"
          class="flex flex-col justify-center items-center px-4 py-10 text-center"
        >
          <div
            class="flex justify-center items-center mb-3 size-11 rounded-full border border-n-weak bg-n-alpha-2 text-n-slate-10"
            aria-hidden="true"
          >
            <i class="i-lucide-message-circle size-5" />
          </div>
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ t('INTERNAL_CHAT.NO_ROOMS') }}
          </p>
          <p class="mb-0 text-xs text-n-slate-10">
            {{ t('INTERNAL_CHAT.ADD_CONVERSATION') }}
          </p>
        </div>
        <ul v-else class="p-0 m-0 list-none">
          <li v-for="room in filteredRooms" :key="room.id">
            <button
              type="button"
              class="flex gap-3 items-center w-full px-3 py-2.5 text-sm text-start border-0 border-b bg-transparent border-n-weak/70 transition-colors hover:bg-n-alpha-2"
              :class="{
                'bg-n-teal-2': selectedRoomId === room.id,
              }"
              @click="selectRoom(room.id)"
            >
              <Avatar
                v-if="room.kind === 'multi_user' && groupRoomListAvatarUrl(room)"
                class="flex-shrink-0"
                :name="room.display_name"
                :src="groupRoomListAvatarUrl(room)"
                :size="40"
                rounded-full
              />
              <div
                v-else-if="room.kind === 'multi_user'"
                class="flex flex-shrink-0 justify-center items-center size-10 rounded-full text-sm font-semibold leading-none"
                :class="groupRoomListColorClass(room)"
              >
                {{ groupRoomListInitial(room) }}
              </div>
              <div
                v-else-if="headerMembersForRoom(room).length"
                class="flex flex-shrink-0 items-center -space-x-2"
              >
                <div
                  v-for="(m, idx) in headerMembersForRoom(room).slice(0, 3)"
                  :key="`${room.id}-${idx}`"
                  class="ring-2 ring-n-background rounded-full"
                >
                  <Avatar
                    :name="m.name"
                    :src="m.avatar_url || ''"
                    :size="40"
                    rounded-full
                  />
                </div>
              </div>
              <Avatar
                v-else
                class="flex-shrink-0"
                :name="room.display_name"
                src=""
                :size="40"
                rounded-full
              />
              <div class="flex flex-col flex-1 gap-0.5 min-w-0">
                <div class="flex gap-2 justify-between items-start min-w-0">
                  <span class="inline-flex gap-1.5 items-center min-w-0 font-semibold truncate text-n-slate-12">
                    <span class="truncate">{{ room.display_name }}</span>
                    <span
                      v-if="room.kind === 'multi_user'"
                      class="i-lucide-users size-3.5 text-n-slate-10 flex-shrink-0"
                      aria-hidden="true"
                    />
                  </span>
                  <span
                    v-if="room.last_message_at"
                    class="flex-shrink-0 text-xxs text-n-slate-10 whitespace-nowrap tabular-nums"
                  >
                    {{ formatRelativeListTime(room.last_message_at) }}
                  </span>
                </div>
                <div v-if="roomLastMessagePreview(room)" class="flex gap-2 items-center min-w-0 w-full">
                  <p
                    class="flex-1 min-w-0 mb-0 text-xs truncate text-start"
                    :class="
                      roomUnreadCount(room) > 0
                        ? 'font-medium text-n-slate-12'
                        : 'text-n-slate-11'
                    "
                  >
                    <span
                      v-if="roomLastMessageHasImageAttachment(room)"
                      class="inline-block align-middle i-lucide-image size-3.5 mr-1"
                      aria-hidden="true"
                    />
                    {{ roomLastMessagePreview(room) }}
                  </p>
                  <span
                    v-if="roomUnreadCount(room) > 0"
                    class="inline-flex flex-shrink-0 items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold tabular-nums bg-n-teal-9 text-white"
                  >
                    {{ roomUnreadCount(room) > 99 ? '99+' : roomUnreadCount(room) }}
                  </span>
                </div>
              </div>
            </button>
          </li>
        </ul>
      </div>
    </aside>

    <div
      class="flex flex-row flex-1 min-w-0 min-h-0 items-stretch overflow-hidden"
    >
      <section class="flex flex-col flex-1 min-w-0 min-h-0">
        <div
          v-if="selectedRoom"
          class="flex flex-col flex-1 min-h-0 min-w-0"
        >
        <header
          class="flex flex-shrink-0 gap-3 justify-between items-center px-4 py-3 border-b border-n-weak"
        >
          <Avatar
            v-if="selectedRoom.kind === 'multi_user' && selectedGroupAvatarUrl"
            class="flex-shrink-0"
            :name="selectedRoom.display_name"
            :src="selectedGroupAvatarUrl"
            :size="36"
            rounded-full
          />
          <div
            v-else-if="selectedRoom.kind === 'multi_user'"
            class="flex flex-shrink-0 justify-center items-center size-9 rounded-full text-sm font-semibold leading-none"
            :class="groupRoomListColorClass(selectedRoom)"
          >
            {{ groupRoomListInitial(selectedRoom) }}
          </div>
          <div
            v-else-if="selectedRoomHeaderMembers.length"
            class="flex flex-shrink-0 items-center -space-x-2"
          >
            <div
              v-for="(m, idx) in selectedRoomHeaderMembers"
              :key="`${m.name}-${idx}`"
              class="ring-2 ring-n-background rounded-full"
            >
              <Avatar
                :name="m.name"
                :src="m.avatar_url || ''"
                :size="36"
                rounded-full
              />
            </div>
          </div>
          <Avatar
            v-else
            class="flex-shrink-0"
            :name="selectedRoom.display_name"
            src=""
            :size="36"
            rounded-full
          />
          <div class="flex flex-1 gap-2 items-center min-w-0">
            <div class="flex flex-1 gap-1.5 items-center min-w-0">
              <h1
                class="min-w-0 text-base font-semibold truncate text-n-slate-12"
              >
                {{ selectedRoom.display_name }}
              </h1>
              <button
                type="button"
                class="text-xs font-medium whitespace-nowrap underline text-n-slate-11 hover:text-n-slate-12"
                @click="openConversationDetailsPanel"
              >
                {{ INTERNAL_CHAT_CONVERSATION_DETAILS_PT.OPEN_LINK }}
              </button>
            </div>
            <Button
              v-if="canEditSelectedGroupName"
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-pencil"
              class="flex-shrink-0 rounded-lg"
              :title="INTERNAL_CHAT_EDIT_GROUP_NAME_PT.ARIA_EDIT"
              :aria-label="INTERNAL_CHAT_EDIT_GROUP_NAME_PT.ARIA_EDIT"
              @click="openEditGroupNameDialog"
            />
            <Button
              v-if="canEditSelectedGroupName"
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-users"
              class="flex-shrink-0 rounded-lg"
              :title="INTERNAL_CHAT_MANAGE_MEMBERS_PT.ARIA_OPEN"
              :aria-label="INTERNAL_CHAT_MANAGE_MEMBERS_PT.ARIA_OPEN"
              :is-loading="loadingManageMembers"
              :disabled="loadingManageMembers"
              @click="openManageMembersDialog"
            />
          </div>
          <div
            v-if="selectedRoom.kind === 'multi_user'"
            class="flex flex-shrink-0 gap-3 items-center"
          >
            <div
              v-if="selectedRoomHeaderMembers.length"
              class="hidden md:flex items-center -space-x-2"
            >
              <div
                v-for="(m, idx) in selectedRoomHeaderMembers"
                :key="`header-right-${m.name}-${idx}`"
                class="ring-2 ring-n-background rounded-full"
              >
                <Avatar
                  :name="m.name"
                  :src="m.avatar_url || ''"
                  :size="30"
                  rounded-full
                />
              </div>
            </div>
            <Button
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-log-out"
              :label="t('INTERNAL_CHAT.LEAVE_GROUP')"
              :aria-label="t('INTERNAL_CHAT.LEAVE_GROUP')"
              @click="openLeaveGroupDialog"
            />
          </div>
          <div
            v-else-if="canShowAssignFunnelCardButton"
            class="flex flex-shrink-0 gap-2 items-center"
          >
            <Button
              type="button"
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-columns-3"
              :label="peerKanbanButtonLabel"
              :aria-label="peerKanbanButtonAria"
              @click.stop="openPeerKanbanPanel"
            />
          </div>
        </header>

        <div
          ref="messagesPaneRef"
          class="overflow-y-auto no-scrollbar flex-1 gap-3 p-4 min-h-0"
        >
          <div
            v-if="currentPinnedMessage"
            class="sticky top-0 z-20 mb-3"
          >
            <div class="flex items-center gap-2 px-3 py-2 rounded-xl border border-n-weak bg-n-background shadow-sm">
              <span class="i-lucide-pin size-4 text-n-brand shrink-0" aria-hidden="true" />
              <button
                type="button"
                class="flex flex-col flex-1 min-w-0 text-left bg-transparent border-0 p-0"
                @click="jumpToPinnedMessage"
              >
                <span class="inline-flex items-center gap-1.5 text-xxs font-medium text-n-brand">
                  {{
                    messageIsAnnouncementPinned(currentPinnedMessage)
                      ? INTERNAL_CHAT_PINNED_MESSAGE_PT.BANNER_TITLE_ANNOUNCEMENT
                      : INTERNAL_CHAT_PINNED_MESSAGE_PT.BANNER_TITLE
                  }}
                  <span
                    v-if="messageIsAnnouncementPinned(currentPinnedMessage)"
                    class="inline-flex px-1.5 py-px text-[10px] rounded-full bg-n-brand/10 text-n-brand"
                  >
                    {{ INTERNAL_CHAT_PINNED_MESSAGE_PT.BADGE_ANNOUNCEMENT }}
                  </span>
                </span>
                <span class="text-xs truncate text-n-slate-12">
                  {{ pinnedMessagePreviewText(currentPinnedMessage) }}
                </span>
              </button>
              <div
                v-if="pinnedMessagesCount > 1"
                class="inline-flex items-center gap-1.5 flex-shrink-0"
              >
                <Button
                  variant="ghost"
                  color="slate"
                  size="sm"
                  icon="i-lucide-chevron-left"
                  class="rounded-lg"
                  aria-label="Mensagem fixada anterior"
                  title="Mensagem fixada anterior"
                  @click="goToPreviousPinnedMessage"
                />
                <span class="text-xxs tabular-nums text-n-slate-10 min-w-[2.2rem] text-center">
                  {{ currentPinnedBannerPosition }}/{{ pinnedMessagesCount }}
                </span>
                <Button
                  variant="ghost"
                  color="slate"
                  size="sm"
                  icon="i-lucide-chevron-right"
                  class="rounded-lg"
                  aria-label="Próxima mensagem fixada"
                  title="Próxima mensagem fixada"
                  @click="goToNextPinnedMessage"
                />
              </div>
            </div>
          </div>
          <div v-if="loadingMessages" class="text-sm text-n-slate-11">
            {{ t('INTERNAL_CHAT.LOADING') }}
          </div>
          <div v-else class="flex flex-col gap-3">
            <template v-for="(msg, index) in messages" :key="msg.id">
              <div
                v-if="shouldShowDateBadge(msg, index)"
                class="flex justify-center"
              >
                <span
                  class="inline-flex px-3 py-1 text-xxs font-medium rounded-full border border-n-weak bg-n-surface-1 text-n-slate-11"
                >
                  {{ messageDateBadgeLabel(msg.created_at) }}
                </span>
              </div>
              <div
                class="flex group"
                :class="
                  msg.sender?.id === currentUser?.id
                    ? 'justify-end'
                    : 'justify-start'
                "
              >
                <div
                  class="relative max-w-[min(560px,85%)] rounded-xl px-3 py-2 border border-n-weak"
                  :data-message-id="msg.id"
                  :style="
                    highlightedMessageId === msg.id
                      ? { opacity: 0.6, transition: 'opacity 120ms ease' }
                      : { opacity: 1, transition: 'opacity 220ms ease' }
                  "
                  :class="
                    isOwnMessage(msg)
                      ? messageIsDeleted(msg)
                        ? 'bg-n-ruby-9 text-white border-transparent'
                        : 'bg-n-brand text-white border-transparent'
                      : 'bg-n-alpha-2 text-n-slate-12'
                  "
                  @contextmenu="event => openMessageContextMenu(event, msg)"
                >
                  <div
                    v-if="!messageIsDeleted(msg)"
                    class="absolute top-1/2 -translate-y-1/2 z-10"
                    :class="isOwnMessage(msg) ? '-left-8' : '-right-8'"
                  >
                    <button
                      type="button"
                      class="inline-flex items-center justify-center rounded-full p-1 border border-n-weak bg-n-background text-n-slate-11 shadow-sm transition-opacity opacity-0 group-hover:opacity-100"
                      aria-label="Reagir"
                      @click.stop="toggleReactionPicker(msg)"
                    >
                      <span class="i-lucide-smile-plus size-3.5" aria-hidden="true" />
                    </button>
                    <div
                      v-if="reactionPickerMessageId === msg.id"
                      class="absolute z-20 bottom-full mb-1.5 left-0 inline-flex gap-1 items-center px-1.5 py-1 rounded-full border shadow-sm border-n-weak bg-n-background"
                    >
                      <button
                        v-for="emoji in INTERNAL_CHAT_REACTION_EMOJIS"
                        :key="`${msg.id}-${emoji}`"
                        type="button"
                        class="inline-flex justify-center items-center size-6 rounded-full text-sm transition-colors hover:bg-n-alpha-2"
                        :class="
                          currentUserReactionEmoji(msg) === emoji
                            ? 'bg-n-alpha-2'
                            : ''
                        "
                        @click.stop="selectMessageReaction(msg, emoji)"
                      >
                        {{ emoji }}
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="messageIsPinned(msg)"
                    class="mb-1.5 inline-flex items-center gap-1 text-xxs font-medium"
                    :class="isOwnMessage(msg) ? 'text-white/80' : 'text-n-brand'"
                  >
                    <span class="i-lucide-pin size-3" aria-hidden="true" />
                    {{
                      messageIsAnnouncementPinned(msg)
                        ? INTERNAL_CHAT_PINNED_MESSAGE_PT.BANNER_TITLE_ANNOUNCEMENT
                        : INTERNAL_CHAT_PINNED_MESSAGE_PT.BANNER_TITLE
                    }}
                  </div>
                  <div
                    v-if="msg.sender?.id !== currentUser?.id"
                    class="mb-1 text-xs font-medium opacity-80"
                  >
                    {{ msg.sender?.available_name || msg.sender?.name }}
                  </div>
                  <div
                    v-if="messageReplyTarget(msg)"
                    role="button"
                    tabindex="0"
                    class="px-2 py-1 mb-1 border-l-2 bg-black/10 border-current/50 rounded-r-md cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-current/40"
                    @click="jumpToReplyTarget(msg)"
                    @keydown.enter.prevent="jumpToReplyTarget(msg)"
                  >
                    <p class="m-0 text-xxs font-medium opacity-80">
                      {{
                        messageReplyTarget(msg)?.sender?.available_name ||
                        messageReplyTarget(msg)?.sender?.name ||
                        'Mensagem'
                      }}
                    </p>
                    <p class="m-0 text-xxs opacity-80 line-clamp-1">
                      {{ replyPreviewText(messageReplyTarget(msg)) }}
                    </p>
                  </div>
                  <div class="text-sm whitespace-pre-wrap break-words">
                    {{ messageDisplayContent(msg) }}
                  </div>
                  <div
                    v-if="messageIsDeleted(msg)"
                    class="mt-1 text-xs font-medium"
                    :class="
                      isOwnMessage(msg)
                        ? 'text-n-ruby-3 opacity-50'
                        : 'text-n-slate-10'
                    "
                  >
                    {{ messageDeletedLabel(msg) }}
                  </div>
                  <div
                    v-if="msg.attachments?.length && !messageIsDeleted(msg)"
                    class="flex flex-col gap-1.5 mt-2"
                  >
                    <a
                      v-for="attachment in msg.attachments.filter(att => String(att?.content_type || '').startsWith('image/'))"
                      :key="`image-${attachment.id}`"
                      :href="attachment.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="block overflow-hidden rounded-lg border border-n-weak bg-n-alpha-1 max-w-[320px]"
                    >
                      <img
                        :src="attachment.url"
                        :alt="attachment.filename || 'image'"
                        class="block object-cover w-full h-auto max-h-[260px]"
                      />
                    </a>
                    <a
                      v-for="attachment in msg.attachments.filter(att => !String(att?.content_type || '').startsWith('image/'))"
                      :key="`file-${attachment.id}`"
                      :href="attachment.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex gap-2 justify-between items-center px-2.5 py-2 rounded-lg border border-n-weak bg-n-background/80 text-n-slate-12 no-underline transition-colors hover:bg-n-background max-w-[320px]"
                    >
                      <span class="flex gap-2 items-center min-w-0">
                        <span
                          class="inline-flex justify-center items-center text-xxs font-semibold rounded-md size-7 shrink-0 tabular-nums"
                          :class="
                            extensionPreviewColorClass(
                              attachmentExtensionLabel(attachment)
                            )
                          "
                        >
                          {{ attachmentExtensionLabel(attachment) }}
                        </span>
                        <span class="flex flex-col min-w-0 leading-tight">
                          <span class="text-xs font-medium truncate">
                            {{ attachment.filename }}
                          </span>
                          <span
                            v-if="formatAttachmentSize(attachment.byte_size)"
                            class="text-xxs text-n-slate-10"
                          >
                            {{ formatAttachmentSize(attachment.byte_size) }}
                          </span>
                        </span>
                      </span>
                      <span
                        class="flex-shrink-0 i-lucide-download size-4 text-n-slate-10"
                        aria-hidden="true"
                      />
                    </a>
                  </div>
                  <div
                    class="mt-1 text-xxs"
                    :class="
                      msg.sender?.id === currentUser?.id
                        ? 'flex items-center justify-end gap-1'
                        : 'text-left'
                    "
                  >
                    <span class="opacity-70">{{ formatTime(msg.created_at) }}</span>
                    <span
                      v-if="msg.sender?.id === currentUser?.id"
                      :class="`${messageReadCheckIconClass(msg)} opacity-70`"
                      aria-hidden="true"
                    />
                  </div>
                  <div
                    v-if="messageReactionSummary(msg).length"
                    class="absolute -bottom-3.5 inline-flex gap-1 items-center px-2 py-0.5 text-xxs rounded-full border shadow-sm"
                    :class="
                      isOwnMessage(msg)
                        ? 'right-2 border-n-weak bg-n-background text-n-slate-12'
                        : 'left-2 border-n-weak bg-n-background text-n-slate-12'
                    "
                  >
                    <span
                      v-for="emoji in messageReactionBadgeEmojis(msg)"
                      :key="`${msg.id}-badge-${emoji}`"
                      class="leading-none"
                    >
                      {{ emoji }}
                    </span>
                    <span class="font-medium tabular-nums opacity-80">
                      {{ messageReactionTotalCount(msg) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div
          v-if="showMessageContextMenu"
          ref="messageContextMenuRef"
          class="fixed z-[300] min-w-[160px] p-1 rounded-lg border shadow-lg border-n-weak bg-n-background"
          :style="{
            left: `${messageContextMenuX}px`,
            top: `${messageContextMenuY}px`,
          }"
        >
          <button
            v-if="contextMenuMessage() && messageCanReply(contextMenuMessage())"
            type="button"
            class="flex gap-2 items-center px-2.5 py-2 w-full text-sm rounded-md text-left text-n-slate-12 hover:bg-n-alpha-2"
            @click="handleReplyFromContextMenu"
          >
            <span class="i-lucide-reply size-4" aria-hidden="true" />
            Responder
          </button>
          <button
            v-if="contextMenuMessage() && messageIsDeleted(contextMenuMessage())"
            type="button"
            class="flex gap-2 items-center px-2.5 py-2 w-full text-sm rounded-md text-left text-n-slate-12 hover:bg-n-alpha-2"
            @click="handleResendFromContextMenu"
          >
            <span class="i-lucide-send size-4" aria-hidden="true" />
            Reenviar mensagem
          </button>
          <button
            v-if="
              contextMenuMessage() &&
              !messageIsDeleted(contextMenuMessage()) &&
              (!messageIsPinned(contextMenuMessage()) ||
                canUnpinMessage(contextMenuMessage()))
            "
            type="button"
            class="flex gap-2 items-center px-2.5 py-2 w-full text-sm rounded-md text-left text-n-slate-12 hover:bg-n-alpha-2"
            @click="handleTogglePinFromContextMenu"
          >
            <span class="i-lucide-pin size-4" aria-hidden="true" />
            {{
              messageIsPinned(contextMenuMessage())
                ? INTERNAL_CHAT_PINNED_MESSAGE_PT.CONTEXT_UNPIN
                : INTERNAL_CHAT_PINNED_MESSAGE_PT.CONTEXT_PIN
            }}
          </button>
          <button
            v-if="contextMenuMessage() && !messageIsDeleted(contextMenuMessage())"
            type="button"
            class="flex gap-2 items-center px-2.5 py-2 w-full text-sm rounded-md text-left text-n-slate-12 hover:bg-n-alpha-2"
            @click="handleEditFromContextMenu"
          >
            <span class="i-lucide-pencil size-4" aria-hidden="true" />
            Editar mensagem
          </button>
          <button
            v-if="contextMenuMessage() && !messageIsDeleted(contextMenuMessage())"
            type="button"
            class="flex gap-2 items-center px-2.5 py-2 w-full text-sm rounded-md text-left text-n-ruby-11 hover:bg-n-alpha-2"
            @click="handleDeleteFromContextMenu"
          >
            <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
            Excluir mensagem
          </button>
        </div>

        <footer
          class="flex-shrink-0 p-4 border-t border-n-weak bg-n-background"
        >
          <div class="flex flex-col gap-2">
            <div
              v-if="composerReplyToMessage"
              class="flex gap-2 justify-between items-start px-2.5 py-2 rounded-lg border border-n-weak bg-n-alpha-2"
            >
              <div class="min-w-0">
                <p class="m-0 text-xxs font-medium text-n-slate-12">
                  Respondendo para {{ composerReplyToMessage.senderName || 'Mensagem' }}
                </p>
                <p class="m-0 text-xs text-n-slate-11 line-clamp-1">
                  {{ composerReplyToMessage.content }}
                </p>
              </div>
              <button
                type="button"
                class="inline-flex items-center p-0.5 rounded text-n-slate-10 hover:text-n-slate-12"
                aria-label="Cancelar resposta"
                @click="clearComposerReply"
              >
                <span class="i-lucide-x size-3.5" aria-hidden="true" />
              </button>
            </div>
            <div class="editor-wrap w-full min-w-0">
              <WootMessageEditor
                ref="composerEditorRef"
                :id="`internal-chat-composer-${selectedRoomId}`"
                :key="`internal-chat-composer-${selectedRoomId}`"
                v-model="composerMessage"
                class="message-editor min-h-[10rem] [&>div]:px-1"
                channel-type="Context::Default"
                :focus-on-mount="false"
                enter-sends-message
                :placeholder="t('INTERNAL_CHAT.PLACEHOLDER_MESSAGE')"
                @submit="sendMessage"
              />
            </div>
            <div
              v-if="composerAttachments.length"
              class="flex flex-wrap gap-2"
            >
              <div
                v-for="(file, index) in composerAttachments"
                :key="`${file.name}-${file.size}-${index}`"
                class="inline-flex gap-1.5 items-center px-2 py-1 text-xs rounded-lg bg-n-alpha-2 text-n-slate-11"
              >
                <span class="i-lucide-paperclip size-3.5" aria-hidden="true" />
                <span class="max-w-[220px] truncate">{{ file.name }}</span>
                <button
                  type="button"
                  class="inline-flex items-center p-0.5 rounded text-n-slate-10 hover:text-n-slate-12"
                  :aria-label="t('INTERNAL_CHAT.REMOVE_ATTACHMENT')"
                  @click="removeComposerAttachment(index)"
                >
                  <span class="i-lucide-x size-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div class="flex gap-2 justify-between items-center">
              <input
                ref="composerAttachmentInputRef"
                type="file"
                multiple
                class="hidden"
                @change="onComposerAttachmentSelected"
              />
              <Button
                variant="faded"
                color="slate"
                icon="i-lucide-paperclip"
                :label="t('INTERNAL_CHAT.ATTACH_FILES')"
                @click="openComposerAttachmentPicker"
              />
              <Button
                color="blue"
                :label="t('INTERNAL_CHAT.SEND')"
                icon="i-lucide-corner-down-left"
                trailing-icon
                :is-loading="sending"
                :disabled="isComposerEmpty || sending"
                @click="sendMessage"
              />
            </div>
          </div>
        </footer>
        </div>

        <div
          v-else
          class="flex flex-1 justify-center items-center p-8"
        >
          <div class="flex flex-col items-center max-w-sm text-center">
            <div
              class="flex justify-center items-center mb-3 size-12 rounded-full border border-n-weak bg-n-alpha-2 text-n-slate-10"
              aria-hidden="true"
            >
              <i class="i-lucide-messages-square size-5" />
            </div>
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ t('INTERNAL_CHAT.EMPTY_SELECT') }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ t('INTERNAL_CHAT.ADD_CONVERSATION') }}
            </p>
          </div>
        </div>
      </section>

      <aside
        v-if="
          (showPeerKanbanPanel ||
            showAssignFunnelPanel ||
            showConversationDetailsPanel) &&
          selectedRoom
        "
        class="flex flex-col min-h-0 min-w-0 w-[min(420px,100%)] max-w-[480px] shrink-0 basis-[min(420px,42vw)] border-l border-n-weak bg-n-background"
      >
        <div
          class="flex flex-shrink-0 gap-2 justify-between items-center px-4 py-3"
        >
          <h2 class="min-w-0 text-sm font-semibold truncate text-n-slate-12">
            {{
              showPeerKanbanPanel
                ? INTERNAL_CHAT_PEER_KANBAN_PT.TITLE
                : showAssignFunnelPanel
                  ? assignFunnelCardDialogTitle
                  : INTERNAL_CHAT_CONVERSATION_DETAILS_PT.PANEL_TITLE
            }}
          </h2>
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            class="flex-shrink-0 rounded-lg"
            :aria-label="
              showPeerKanbanPanel
                ? INTERNAL_CHAT_PEER_KANBAN_PT.CLOSE
                : showAssignFunnelPanel
                  ? INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.CANCEL
                  : INTERNAL_CHAT_CONVERSATION_DETAILS_PT.CLOSE
            "
            @click="closeInternalChatSidePanels"
          />
        </div>
        <InternalChatPeerKanbanPanel
          v-if="showPeerKanbanPanel"
          :hint="INTERNAL_CHAT_PEER_KANBAN_PT.HINT"
          :funnel-label="INTERNAL_CHAT_PEER_KANBAN_PT.FUNNEL_LABEL"
          :stage-label="INTERNAL_CHAT_PEER_KANBAN_PT.STAGE_LABEL"
          :loading-label="INTERNAL_CHAT_PEER_KANBAN_PT.LOADING"
          :empty-label="INTERNAL_CHAT_PEER_KANBAN_PT.EMPTY"
          :updated-label="INTERNAL_CHAT_PEER_KANBAN_PT.UPDATED"
          :stage-since-label="INTERNAL_CHAT_PEER_KANBAN_PT.STAGE_SINCE"
          :attachments-label="INTERNAL_CHAT_PEER_KANBAN_PT.ATTACHMENTS"
          :conversation-label="INTERNAL_CHAT_PEER_KANBAN_PT.CONVERSATION"
          :card-id-label="INTERNAL_CHAT_PEER_KANBAN_PT.CARD_ID"
          :assign-button-label="assignFunnelCardButtonLabel"
          :assign-button-aria="assignFunnelCardButtonAria"
          :funnel-id="peerKanbanFunnelId"
          :funnel-options="peerKanbanFunnelSelectOptions"
          :stage-id="peerKanbanStageId"
          :stage-options="peerKanbanStageSelectOptions"
          :items="peerKanbanItems"
          :loading="peerKanbanLoading"
          :item-aria-label="peerKanbanItemAriaLabel"
          :item-meta="peerKanbanItemListMeta"
          :on-open-item="openPeerKanbanItemDetail"
          :translate="t"
          @update:funnel-id="onPeerKanbanFunnelSelectUpdate"
          @update:stage-id="value => { peerKanbanStageId = value; fetchPeerKanbanItems(); }"
          @open-assign="openAssignFunnelPanel"
        />
        <template v-else-if="showAssignFunnelPanel">
          <InternalChatAssignFunnelPanel
            :hint="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.ITEMS_HINT"
            :funnel-label="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.FUNNEL_LABEL"
            :funnel-placeholder="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.FUNNEL_PLACEHOLDER"
            :mode-label="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.MODE_LABEL"
            :mode-add-label="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.MODE_ADD"
            :mode-replace-label="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.MODE_REPLACE"
            :loading-items-label="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.LOADING_ITEMS"
            :empty-items-label="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.EMPTY_ITEMS"
            :cancel-label="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.CANCEL"
            :confirm-label="INTERNAL_CHAT_ASSIGN_FUNNEL_CARD_PT.CONFIRM"
            :funnel-id="assignSelectedFunnelId"
            :funnel-options="assignFunnelSelectOptions"
            :mode="assignMode"
            :loading-items="assignLoadingItems"
            :items="assignKanbanItems"
            :selected-item-ids="assignSelectedItemIds"
            :submitting="assignSubmitting"
            :item-row-parts="assignItemRowParts"
            @update:funnel-id="value => { assignSelectedFunnelId = value; }"
            @update:mode="value => { assignMode = value; }"
            @toggle-item="toggleAssignItem"
            @confirm="handleAssignFunnelCardsConfirm"
            @cancel="closeAssignFunnelPanel"
          />
        </template>
        <InternalChatConversationDetailsPanel
          v-else-if="showConversationDetailsPanel && selectedRoom"
          :room="selectedRoom"
          :agents-by-id="internalChatAgentsById"
          :current-user-id="currentUser?.id"
          :title="INTERNAL_CHAT_CONVERSATION_DETAILS_PT.PANEL_TITLE"
          :info-section-title="INTERNAL_CHAT_CONVERSATION_DETAILS_PT.INFO_SECTION_TITLE"
          :kind-group-label="t('INTERNAL_CHAT.KIND_GROUP')"
          :kind-direct-label="t('INTERNAL_CHAT.KIND_DIRECT')"
          :participants-label="INTERNAL_CHAT_CONVERSATION_DETAILS_PT.PARTICIPANTS"
          :created-by-label="INTERNAL_CHAT_CONVERSATION_DETAILS_PT.CREATED_BY"
          :no-members-label="INTERNAL_CHAT_CONVERSATION_DETAILS_PT.EMPTY_MEMBERS"
          :you-label="INTERNAL_CHAT_CONVERSATION_DETAILS_PT.YOU"
          :kind-label="INTERNAL_CHAT_CONVERSATION_DETAILS_PT.KIND"
          :shared-files-label="INTERNAL_CHAT_CONVERSATION_DETAILS_PT.SHARED_FILES"
          :no-shared-files-label="INTERNAL_CHAT_CONVERSATION_DETAILS_PT.EMPTY_SHARED_FILES"
          :attachments="selectedRoomConversationAttachments"
          :group-avatar-url="selectedGroupAvatarUrl"
          :can-edit-group-avatar="canEditSelectedGroupName"
          :is-saving-group-avatar="savingGroupAvatar"
          @update-group-avatar="handleGroupAvatarUpdate"
          @clear-group-avatar="handleGroupAvatarClear"
        />
      </aside>
    </div>

    <Dialog
      ref="directDialogRef"
      type="edit"
      width="md"
      :title="t('INTERNAL_CHAT.NEW_DIRECT')"
      :confirm-button-label="t('INTERNAL_CHAT.NEW_DIRECT')"
      :is-loading="creatingDirect"
      :disable-confirm-button="!directUserId || creatingDirect"
      @confirm="handleDirectConfirm"
    >
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-11">{{
          t('INTERNAL_CHAT.SELECT_AGENT')
        }}</label>
        <div class="relative">
          <span
            class="pointer-events-none absolute i-lucide-search size-4 top-1/2 left-3 -translate-y-1/2 text-n-slate-10"
            aria-hidden="true"
          />
          <input
            v-model="directAgentSearchQuery"
            type="search"
            autocomplete="off"
            class="reset-base w-full h-9 py-2 pr-3 pl-9 text-sm rounded-lg border outline-none border-n-weak bg-n-alpha-2 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-n-brand focus:border-transparent"
            :placeholder="t('INTERNAL_CHAT.SEARCH_AGENT')"
          />
        </div>
        <ul
          class="overflow-y-auto max-h-52 p-0 m-0 list-none rounded-lg border divide-y divide-n-weak border-n-weak"
          role="listbox"
        >
          <li
            v-if="filteredAgentsForDirect.length === 0"
            class="px-3 py-4 text-sm text-center text-n-slate-11"
          >
            {{ t('INTERNAL_CHAT.NO_AGENTS_MATCH') }}
          </li>
          <li
            v-for="a in filteredAgentsForDirect"
            :key="a.id"
            role="option"
            :aria-selected="String(directUserId) === String(a.id)"
            class="flex gap-2 items-center px-3 py-2.5 transition-colors cursor-pointer hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-2 ring-1 ring-inset ring-n-brand':
                String(directUserId) === String(a.id),
            }"
            @click="directUserId = String(a.id)"
          >
            <Avatar
              :name="a.available_name || a.name"
              :src="a.thumbnail || a.avatar_url || ''"
              :size="32"
              rounded-full
            />
            <div class="flex flex-col flex-1 gap-0 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ a.available_name || a.name }}
              </span>
              <span v-if="a.email" class="text-xs truncate text-n-slate-10">
                {{ a.email }}
              </span>
            </div>
          </li>
        </ul>
      </div>
    </Dialog>

    <Dialog
      ref="leaveGroupDialogRef"
      type="alert"
      width="sm"
      :title="t('INTERNAL_CHAT.LEAVE_GROUP_DIALOG_TITLE')"
      :description="t('INTERNAL_CHAT.LEAVE_GROUP_DIALOG_DESCRIPTION')"
      :confirm-button-label="t('INTERNAL_CHAT.LEAVE_GROUP')"
      :is-loading="leavingGroup"
      :disable-confirm-button="leavingGroup"
      @confirm="handleLeaveGroupConfirm"
    />

    <Dialog
      ref="editGroupNameDialogRef"
      type="edit"
      width="md"
      :title="INTERNAL_CHAT_EDIT_GROUP_NAME_PT.TITLE"
      :confirm-button-label="INTERNAL_CHAT_EDIT_GROUP_NAME_PT.SAVE"
      :cancel-button-label="INTERNAL_CHAT_EDIT_GROUP_NAME_PT.CANCEL"
      :is-loading="savingGroupName"
      :disable-confirm-button="!editGroupName.trim() || savingGroupName"
      @confirm="handleEditGroupNameConfirm"
    >
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-11">{{
          INTERNAL_CHAT_EDIT_GROUP_NAME_PT.LABEL
        }}</label>
        <input
          v-model="editGroupName"
          type="text"
          autocomplete="off"
          class="w-full px-3 py-2 text-sm rounded-lg border outline-none bg-n-alpha-2 border-n-weak text-n-slate-12"
          :placeholder="INTERNAL_CHAT_EDIT_GROUP_NAME_PT.PLACEHOLDER"
        />
      </div>
    </Dialog>

    <Dialog
      ref="manageMembersDialogRef"
      type="edit"
      width="md"
      :title="INTERNAL_CHAT_MANAGE_MEMBERS_PT.TITLE"
      :show-confirm-button="false"
      :cancel-button-label="INTERNAL_CHAT_MANAGE_MEMBERS_PT.CLOSE"
    >
      <div
        class="flex flex-col gap-5 max-h-[min(70vh,520px)] overflow-y-auto no-scrollbar"
      >
        <div class="flex flex-col gap-2">
          <h4 class="text-sm font-medium text-n-slate-11">
            {{ INTERNAL_CHAT_MANAGE_MEMBERS_PT.IN_GROUP }}
          </h4>
          <ul
            class="p-0 m-0 list-none rounded-lg border divide-y divide-n-weak border-n-weak"
          >
            <li
              v-for="m in manageGroupMembersSorted"
              :key="m.id"
              class="flex gap-2 items-center px-3 py-2.5"
            >
              <Avatar
                :name="m.available_name || m.name"
                :src="m.thumbnail || m.avatar_url || ''"
                :size="32"
                rounded-full
              />
              <div class="flex flex-col flex-1 gap-0 min-w-0">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ m.available_name || m.name }}
                </span>
                <span
                  v-if="m.member_role === 'admin' || m.member_role === 0"
                  class="text-xs text-n-slate-10"
                >
                  {{ INTERNAL_CHAT_MANAGE_MEMBERS_PT.ADMIN }}
                </span>
              </div>
              <Button
                variant="faded"
                color="ruby"
                size="sm"
                :label="INTERNAL_CHAT_MANAGE_MEMBERS_PT.REMOVE"
                :aria-label="INTERNAL_CHAT_MANAGE_MEMBERS_PT.REMOVE_ARIA"
                :is-loading="memberActionLoadingId === m.id"
                :disabled="memberActionLoadingId != null"
                @click="handleRemoveMemberFromGroup(m.id)"
              />
            </li>
          </ul>
        </div>
        <div class="flex flex-col gap-2">
          <h4 class="text-sm font-medium text-n-slate-11">
            {{ INTERNAL_CHAT_MANAGE_MEMBERS_PT.ADD_SECTION }}
          </h4>
          <div class="relative">
            <span
              class="pointer-events-none absolute i-lucide-search size-4 top-1/2 left-3 -translate-y-1/2 text-n-slate-10"
              aria-hidden="true"
            />
            <input
              v-model="manageMembersAgentSearchQuery"
              type="search"
              autocomplete="off"
              class="reset-base w-full h-9 py-2 pr-3 pl-9 text-sm rounded-lg border outline-none border-n-weak bg-n-alpha-2 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-n-brand focus:border-transparent"
              :placeholder="INTERNAL_CHAT_MANAGE_MEMBERS_PT.SEARCH"
            />
          </div>
          <ul
            class="overflow-y-auto max-h-48 no-scrollbar p-0 m-0 list-none rounded-lg border divide-y divide-n-weak border-n-weak"
          >
            <li
              v-if="agentsAvailableToAddToGroup.length === 0"
              class="px-3 py-4 text-sm text-center text-n-slate-11"
            >
              {{ INTERNAL_CHAT_MANAGE_MEMBERS_PT.EMPTY_ADD }}
            </li>
            <li
              v-for="a in agentsAvailableToAddToGroup"
              :key="a.id"
              class="flex gap-2 items-center px-3 py-2.5"
            >
              <Avatar
                :name="a.available_name || a.name"
                :src="a.thumbnail || a.avatar_url || ''"
                :size="32"
                rounded-full
              />
              <div class="flex flex-col flex-1 gap-0 min-w-0">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ a.available_name || a.name }}
                </span>
                <span v-if="a.email" class="text-xs truncate text-n-slate-10">
                  {{ a.email }}
                </span>
              </div>
              <Button
                color="blue"
                size="sm"
                :label="INTERNAL_CHAT_MANAGE_MEMBERS_PT.ADD"
                :is-loading="memberActionLoadingId === a.id"
                :disabled="memberActionLoadingId != null"
                @click="handleAddMemberToGroup(a.id)"
              />
            </li>
          </ul>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="editMessageDialogRef"
      type="edit"
      width="md"
      title="Editar mensagem"
      confirm-button-label="Salvar"
      cancel-button-label="Cancelar"
      :is-loading="savingMessageEdit"
      :disable-confirm-button="!editingMessageContent.trim() || savingMessageEdit"
      @confirm="handleEditMessageConfirm"
    >
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-11">Mensagem</label>
        <textarea
          v-model="editingMessageContent"
          rows="4"
          class="w-full px-3 py-2 text-sm rounded-lg border outline-none resize-y bg-n-alpha-2 border-n-weak text-n-slate-12"
        />
      </div>
    </Dialog>

    <Dialog
      ref="deleteMessageDialogRef"
      type="alert"
      width="sm"
      title="Excluir mensagem"
      description="Essa acao nao pode ser desfeita."
      confirm-button-label="Excluir"
      :is-loading="deletingMessage"
      :disable-confirm-button="deletingMessage"
      @confirm="handleDeleteMessageConfirm"
    />

    <Dialog
      ref="resendMessageDialogRef"
      type="alert"
      width="sm"
      title="Reenviar mensagem"
      description="Deseja reenviar esta mensagem excluida?"
      confirm-button-label="Reenviar"
      :is-loading="resendingMessage"
      :disable-confirm-button="resendingMessage"
      @confirm="handleResendMessageConfirm"
    />

    <Dialog
      ref="pinMessageDialogRef"
      type="edit"
      width="sm"
      :title="INTERNAL_CHAT_PINNED_MESSAGE_PT.CONFIRM_TITLE"
      :description="INTERNAL_CHAT_PINNED_MESSAGE_PT.CONFIRM_DESCRIPTION"
      :confirm-button-label="INTERNAL_CHAT_PINNED_MESSAGE_PT.CONFIRM_ACTION"
      :disable-confirm-button="!pinTargetMessage"
      @confirm="handlePinMessageConfirm"
    >
      <div class="flex flex-col gap-3">
        <p class="m-0 text-sm text-n-slate-11 line-clamp-2">
          {{ pinnedMessagePreviewText(pinTargetMessage) }}
        </p>
        <div class="flex flex-col gap-2">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="pinConfirmMode"
              type="radio"
              value="normal"
              class="rounded-full border-n-weak text-n-brand focus:ring-n-brand"
            />
            <span>{{ INTERNAL_CHAT_PINNED_MESSAGE_PT.MODE_NORMAL }}</span>
          </label>
          <label
            v-if="selectedRoom?.kind === 'multi_user'"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              v-model="pinConfirmMode"
              type="radio"
              value="announcement"
              class="rounded-full border-n-weak text-n-brand focus:ring-n-brand"
            />
            <span>{{ INTERNAL_CHAT_PINNED_MESSAGE_PT.MODE_ANNOUNCEMENT }}</span>
          </label>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="groupDialogRef"
      type="edit"
      width="md"
      :title="t('INTERNAL_CHAT.NEW_GROUP')"
      :confirm-button-label="t('INTERNAL_CHAT.NEW_GROUP')"
      :is-loading="creatingGroup"
      :disable-confirm-button="
        !groupName.trim() || groupMemberIds.length === 0 || creatingGroup
      "
      @confirm="handleGroupConfirm"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-11">{{
            t('INTERNAL_CHAT.GROUP_NAME_LABEL')
          }}</label>
          <input
            v-model="groupName"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border outline-none bg-n-alpha-2 border-n-weak text-n-slate-12"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-11">{{
            t('INTERNAL_CHAT.GROUP_MEMBERS_HINT')
          }}</label>
          <div class="relative">
            <span
              class="pointer-events-none absolute i-lucide-search size-4 top-1/2 left-3 -translate-y-1/2 text-n-slate-10"
              aria-hidden="true"
            />
            <input
              v-model="groupAgentSearchQuery"
              type="search"
              autocomplete="off"
              class="reset-base w-full h-9 py-2 pr-3 pl-9 text-sm rounded-lg border outline-none border-n-weak bg-n-alpha-2 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-n-brand focus:border-transparent"
              :placeholder="t('INTERNAL_CHAT.SEARCH_AGENT')"
            />
          </div>
          <ul
            class="overflow-y-auto max-h-52 p-0 m-0 list-none rounded-lg border divide-y divide-n-weak border-n-weak"
            role="list"
          >
            <li
              v-if="filteredAgentsForGroup.length === 0"
              class="px-3 py-4 text-sm text-center text-n-slate-11"
            >
              {{ t('INTERNAL_CHAT.NO_AGENTS_MATCH') }}
            </li>
            <li v-for="a in filteredAgentsForGroup" :key="a.id">
              <label
                class="flex gap-3 items-center px-3 py-2.5 transition-colors cursor-pointer hover:bg-n-alpha-2"
              >
                <input
                  type="checkbox"
                  class="rounded border-n-weak shrink-0 text-n-brand focus:ring-n-brand"
                  :checked="isGroupMemberChecked(a.id)"
                  @change="toggleGroupMember(a.id)"
                />
                <Avatar
                  :name="a.available_name || a.name"
                  :src="a.thumbnail || a.avatar_url || ''"
                  :size="32"
                  rounded-full
                />
                <div class="flex flex-col flex-1 gap-0 min-w-0">
                  <span class="text-sm font-medium truncate text-n-slate-12">
                    {{ a.available_name || a.name }}
                  </span>
                  <span v-if="a.email" class="text-xs truncate text-n-slate-10">
                    {{ a.email }}
                  </span>
                </div>
              </label>
            </li>
          </ul>
        </div>
      </div>
    </Dialog>
  </div>
</template>
